import { CoreTypes, hasFormatting, normalizeSpans, readSpans, spansText, type ConnectorEnd, type Id, type ObjectAuthor, type ObjectRecord, type Style, type TextSpan } from '@vp/shared-types';
import {
  boxBounds,
  localToWorld,
  commands as cmd,
  CoordinateLimitError,
  keyBetween,
  keysBetween,
  makeObject,
  MAX_ZOOM,
  MIN_ZOOM,
  DEFAULT_TEXT_STYLE,
  defaultStyle,
  normalizeRect,
  rectContainsRect,
  rectsIntersect,
  snap,
  ReadOnlyError,
  UndoBlockedError,
  worldToLocal,
  type Box,
  type CanvasDocument,
  type ClipPayload,
  type History,
  type Rect,
  type TxContext,
  type Vec2,
} from '@vp/core';
import type { GridSettings } from '../backend';
import type { Renderer } from '../renderer';
import type { Scene } from '../scene';
import { arrowSize, flattenBezier, pointInPolygon } from '../pathgeom';
import {
  clampPenWidth,
  DEFAULT_PATH_STYLE,
  DEFAULT_PEN_STYLE,
  finishStroke,
  isBrush,
  isStrokeMode,
  simplifyPolyline,
  stylusMul,
  type PathToolStyle,
  type PenToolStyle,
} from '../brush';
export { simplifyPolyline };
import { isSupportedDiagramVersion, LANG_VERSION } from '@vp/dskhema';
import { diagramFit, diagramMeasure, diagramNaturalSize, layoutDiagram } from '../diagram';
import { planExplode } from './explode';
import { planArrange } from './arrange';
import { booleanPolygons, booleanSupported, objectPolygon, piecesOf, resultStyle, type BooleanOp } from './boolean';
import { flowDirection, looksLikeDskhema, withFlowDirection, type Direction } from '@vp/dskhema';
export type { Direction as FlowDirection } from '@vp/dskhema';
import { dragPart, hitNodePart, insertNode, makeCorner, nearestOnPath, nodeCount, removeNode, type NodePart } from './pathedit';
import { fitArrowLabel, pathLength, type ModuleTextInfo } from '../display';
import { rectForm } from '../forms';
import { fitBoxToContent, measureTextHeight, textBoxHeight } from '../text';
import { bindTo, computeRoute, connectorsAttachedTo, dependentConnectors, isLineLike, pointsBox, rectsOverlap, rerouteConnector, worldPath } from './connectors';
import { cursorFor, handlesFor, hitHandle, type HandleId, type HandleInfo } from './handles';
import { drawOverlay } from './overlay';
import { ViewTrail, VIEW_HISTORY_ON, type ViewSnap } from './view-trail';

export { VIEW_HISTORY_ON };

export interface TypeCaps {
  move: boolean;
  resize: boolean;
  rotate: boolean;
  editText: boolean;
}

export interface EditorHost {
  notify(level: 'info' | 'warning' | 'error', message: string): void;
  /**
   * API 4.6: a key while exactly one object is selected — true when a module took it (the editor then
   * does nothing with it). Escape and Ctrl/Cmd combinations are never offered.
   */
  captureKey?(objectId: Id, e: KeyboardEvent, type: 'down' | 'up'): boolean;
  t(key: string, params?: Record<string, string | number>): string;
  /** Capabilities for module types; core types are handled internally. */
  typeCaps?(type: string): Partial<TypeCaps> | undefined;
  /** API 1.5: module object types with real text (inset, fit, default style). */
  typeText?(type: string): ModuleTextInfo | undefined;
  /** API 2.2: module types whose primitives may be clickable (`capabilities.actions`). */
  typeActions?(type: string): boolean;
  /** API 2.2: a module tool's gesture is done (world points); the tool returns to select. */
  onToolGesture?(tool: ToolDef, gesture: { kind: 'click' | 'line' | 'path'; points: Vec2[] }): void;
  /** API 2.2: the user clicked a clickable area of an object. */
  onAction?(objectId: Id, action: { id: string; data?: string | number | boolean | null }): void;
  /** API 2.0: style of new objects of a module type (manifest `defaultStyle`). */
  typeStyle?(type: string): Partial<Style> | undefined;
  /** Current data format version of a module object type. */
  typeFormatVersion?(type: string): number | undefined;
  /** Initial props for module object types created by tools. */
  createDefaults?(type: string, size: { w: number; h: number }): Promise<Record<string, unknown>>;
  /** Conflict check before a platform operation on a module object (§9.4); returns a message if blocked. */
  conflict?(obj: ObjectRecord, op: 'move' | 'resize' | 'rotate' | 'delete' | 'style'): string | null;
  /**
   * The press landed on the canvas while a floating window is still open.
   * It must not change the selection; closing the window is the UI's job.
   */
  preserveSelection?(e: PointerEvent): boolean;
  /** Profile adding objects in this edit. Stamped on new objects for a future shared canvas. */
  objectAuthor?(): ObjectAuthor | null;
}

export type ToolKind = 'select' | 'hand' | 'lasso' | 'box' | 'text' | 'line' | 'connector' | 'pen' | 'path' | 'eraser' | 'gesture';

export interface ToolDef {
  id: string;
  kind: ToolKind;
  objectType?: string;
  defaultSize?: { w: number; h: number };
  /** Style the tool's objects get on top of the type's default (a capsule is a rounded rectangle). */
  style?: Partial<Style>;
  /** Props the tool's objects start with (a form of a rectangle). */
  props?: Record<string, unknown>;
  /** Module tools: label/icon for the UI. */
  label?: string;
  icon?: string;
  group?: string;
  moduleId?: string;
  /** API 2.2, kind `gesture`: what the tool collects for its module. */
  gesture?: 'click' | 'line' | 'path';
}

export interface EditorSettings {
  grid: GridSettings;
  snapToGrid: boolean;
  marqueeMode: 'contain' | 'intersect';
  /** What a left drag on empty canvas does with the select tool (§4.3, §4.5). */
  emptyDrag: 'marquee' | 'pan';
  /** Double click on empty canvas or inside a frame (§4.3). */
  emptyDblClick: 'text' | 'zoom' | 'none';
  /** Snap moved objects to edges/centers of nearby objects and show guides. */
  smartGuides: boolean;
  /** Drag with the right mouse button moves the canvas; a click without moving still opens the menu. */
  rightDragPan: boolean;
  /** The user's style for new objects of a type ("make default style"); sizes at zoom 100%. */
  defaultStyles: Record<string, Partial<Style>>;
}

export const DEFAULT_SETTINGS: EditorSettings = {
  grid: { enabled: true, kind: 'dots', step: 20, color: '#cbd5e1' },
  snapToGrid: false,
  marqueeMode: 'intersect',
  emptyDrag: 'marquee',
  emptyDblClick: 'text',
  smartGuides: true,
  rightDragPan: false,
  defaultStyles: {},
};

export const CORE_TOOLS: ToolDef[] = [
  { id: 'select', kind: 'select', group: 'nav' },
  { id: 'hand', kind: 'hand', group: 'nav' },
  { id: 'lasso', kind: 'lasso', group: 'nav' },
  { id: 'rect', kind: 'box', objectType: CoreTypes.rect, defaultSize: { w: 160, h: 100 }, group: 'shapes' },
  { id: 'ellipse', kind: 'box', objectType: CoreTypes.ellipse, defaultSize: { w: 140, h: 100 }, group: 'shapes' },
  { id: 'triangle', kind: 'box', objectType: CoreTypes.triangle, defaultSize: { w: 120, h: 100 }, group: 'shapes' },
  { id: 'diamond', kind: 'box', objectType: CoreTypes.diamond, defaultSize: { w: 140, h: 100 }, group: 'shapes' },
  // the radius is clamped to half the shorter side when drawn: fully round ends at any size
  { id: 'pill', kind: 'box', objectType: CoreTypes.rect, defaultSize: { w: 220, h: 64 }, style: { cornerRadius: 10000 }, group: 'shapes' },
  // mind-map bubble: capsule with a pastel fill (the collapse counter sits on its side)
  {
    id: 'node',
    kind: 'box',
    objectType: CoreTypes.rect,
    defaultSize: { w: 240, h: 56 },
    style: {
      cornerRadius: 10000,
      fill: { kind: 'solid', color: '#eee8ff' },
      stroke: { color: '#6d5ce7', width: 2, dash: 'solid' },
      text: { ...DEFAULT_TEXT_STYLE, fontSize: 16 },
    },
    group: 'shapes',
  },
  // flowchart forms of a rectangle (forms.ts)
  { id: 'manualInput', kind: 'box', objectType: CoreTypes.rect, defaultSize: { w: 180, h: 100 }, style: { cornerRadius: 0 }, props: { form: 'manual' }, group: 'shapes' },
  { id: 'data', kind: 'box', objectType: CoreTypes.rect, defaultSize: { w: 180, h: 90 }, style: { cornerRadius: 0 }, props: { form: 'data' }, group: 'shapes' },
  { id: 'document', kind: 'box', objectType: CoreTypes.rect, defaultSize: { w: 200, h: 140 }, style: { cornerRadius: 10 }, props: { form: 'document' }, group: 'shapes' },
  { id: 'display', kind: 'box', objectType: CoreTypes.rect, defaultSize: { w: 200, h: 140 }, style: { cornerRadius: 10 }, props: { form: 'display' }, group: 'shapes' },
  { id: 'bracket', kind: 'box', objectType: CoreTypes.rect, defaultSize: { w: 200, h: 90 }, style: { fill: { kind: 'none' }, text: { ...DEFAULT_TEXT_STYLE, align: 'left' } }, props: { form: 'bracket' }, group: 'shapes' },
  { id: 'brace', kind: 'box', objectType: CoreTypes.rect, defaultSize: { w: 200, h: 120 }, style: { fill: { kind: 'none' }, text: { ...DEFAULT_TEXT_STYLE, align: 'left' } }, props: { form: 'brace' }, group: 'shapes' },
  { id: 'braceLeft', kind: 'box', objectType: CoreTypes.rect, defaultSize: { w: 200, h: 120 }, style: { fill: { kind: 'none' }, text: { ...DEFAULT_TEXT_STYLE, align: 'right' } }, props: { form: 'brace', flip: true }, group: 'shapes' },
  { id: 'text', kind: 'text', objectType: CoreTypes.text, defaultSize: { w: 240, h: 46 }, group: 'text' },
  { id: 'sticker', kind: 'box', objectType: CoreTypes.sticker, defaultSize: { w: 180, h: 180 }, group: 'text' },
  { id: 'frame', kind: 'box', objectType: CoreTypes.frame, defaultSize: { w: 640, h: 400 }, group: 'frame' },
  { id: 'line', kind: 'line', objectType: CoreTypes.line, group: 'lines' },
  { id: 'arrow', kind: 'line', objectType: CoreTypes.line, group: 'lines' },
  { id: 'connector', kind: 'connector', objectType: CoreTypes.connector, group: 'lines' },
  { id: 'path', kind: 'path', objectType: CoreTypes.path, group: 'draw' },
  { id: 'pen', kind: 'pen', objectType: CoreTypes.pen, group: 'draw' },
  { id: 'eraser', kind: 'eraser', group: 'draw' },
];

type EventMap = {
  selection: Id[];
  tool: string;
  history: void;
  textedit: Id | null;
  activate: Id;
  contextmenu: { x: number; y: number; target: Id | null };
  settings: EditorSettings;
  cursor: string;
  /** Id of the bezier path whose nodes are being edited, or null. */
  pathedit: Id | null;
  /** Object under the pointer, or null. */
  hover: Id | null;
  /** Back/forward availability for camera jumps changed. */
  view: void;
};

type Drag =
  | {
      kind: 'pan';
      lastX: number;
      lastY: number;
      /** Screen pixels travelled, so a click can be told from a drag. */
      moved: number;
      /** A press on empty space while typing: a click still leaves text editing, a drag keeps it. */
      clickAway?: boolean;
      /** the right button moves the canvas: how far it went (px), a short click opens the menu */
      right?: { moved: number; x: number; y: number };
    }
  | { kind: 'marquee'; start: Vec2; cur: Vec2; additive: boolean; base: Set<Id>; frameClick?: Id }
  | { kind: 'pending-move'; startScreen: Vec2; startWorld: Vec2; hit: Id; action?: { objectId: Id; id: string; data?: string | number | boolean | null } }
  | { kind: 'move'; tx: TxContext; startWorld: Vec2; orig: Map<Id, { x: number; y: number }>; anchor: Rect; roots: Id[]; targets: Rect[]; guides: Guide[]; gaps?: GapGuide[]; dropFrame?: Id | null; /** Selection before a copy-drag; restored if the drag is cancelled. */ copyFrom?: Id[] }
  | { kind: 'pen'; pts: number[]; pressure?: number[] }
  | { kind: 'gesture'; tool: ToolDef; pts: number[] }
  | { kind: 'node'; tx: TxContext; base: ObjectRecord; orig: number[]; index: number; part: NodePart | 'convert'; start: Vec2; moved: boolean }
  | { kind: 'erase'; last: Vec2; erased: Set<Id> }
  /** Lasso: a free outline (world points, flat); objects inside it are selected as it is drawn. */
  | { kind: 'lasso'; pts: number[]; base: Set<Id> }
  | { kind: 'resize'; tx: TxContext; handle: HandleId; startWorld: Vec2; box: Box; orig: Map<Id, ObjectRecord>; single: boolean; targets: Rect[]; guides: Guide[] }
  | { kind: 'rotate'; tx: TxContext; center: Vec2; startAngle: number; orig: Map<Id, ObjectRecord>; single: boolean }
  | { kind: 'create'; tool: ToolDef; start: Vec2; cur: Vec2 }
  | { kind: 'line'; tool: ToolDef; start: Vec2; cur: Vec2; startBind?: ConnectorEnd; endBind?: ConnectorEnd }
  | { kind: 'endpoint'; tx: TxContext; id: Id; which: 'start' | 'end'; exclude: Set<Id>; bind?: ConnectorEnd }
  /** From a "connected shape" point: a click adds a shape, a drag draws the arrow (to an object or to a new shape at the pointer). */
  | { kind: 'quick'; dir: QuickDir; src: Id; start: Vec2; cur: Vec2; moved: boolean; bind?: ConnectorEnd };

const CLICK_SLOP = 3;

/**
 * Dragging with Ctrl (⌘ on macOS) copies the selection and moves the copy.
 * Ctrl+click on Apple is a right click, so only ⌘ starts a copy there.
 */
function dragCopies(e: { ctrlKey: boolean; metaKey: boolean }): boolean {
  if (e.metaKey) return true;
  if (!e.ctrlKey) return false;
  const platform = typeof navigator === 'undefined' ? '' : navigator.platform;
  return !/Mac|iPhone|iPad|iPod/.test(platform);
}

/**
 * Extra screen px around an expanded branch ring that still count as hovering it.
 * The ring is drawn 10px past its own radius outside the shape, so this must be larger than
 * that gap: otherwise the ring hides as soon as the pointer leaves the shape — usually onto
 * the connector — and flickers under the cursor.
 */
const KNOB_KEEP = 20;

/** Side of a shape a connected shape is added on. */
export type QuickDir = 'up' | 'right' | 'down' | 'left';

/** Distance of the "connected shape" points from the edges of the selected shape (CSS px). */
export const QUICK_OFFSET = 40;
const QUICK_HIT = 11;
/** Shapes that get the points: flowchart blocks (module objects too, see quickSource). */
const QUICK_TYPES = new Set<string>([CoreTypes.rect, CoreTypes.ellipse, CoreTypes.triangle, CoreTypes.diamond, CoreTypes.sticker]);
const OPPOSITE: Record<QuickDir, QuickDir> = { up: 'down', down: 'up', left: 'right', right: 'left' };

/** Middle of a side on the outline of the shape (0..1 of its box). */
function sideAnchor(type: string, dir: QuickDir): { x: number; y: number } {
  if (type === CoreTypes.triangle && (dir === 'left' || dir === 'right')) return { x: dir === 'left' ? 0.25 : 0.75, y: 0.5 };
  return { up: { x: 0.5, y: 0 }, right: { x: 1, y: 0.5 }, down: { x: 0.5, y: 1 }, left: { x: 0, y: 0.5 } }[dir];
}

/** Alignment guide shown while moving (world coordinates). */
export interface Guide {
  axis: 'x' | 'y';
  /** x for vertical guides, y for horizontal ones. */
  at: number;
  from: number;
  to: number;
}

/** Equal spacing shown while moving: a gap from `from` to `to` along the axis, drawn at `at` across it. */
export interface GapGuide {
  axis: 'x' | 'y';
  from: number;
  to: number;
  at: number;
}

/**
 * Interaction engine (§3.1): tools, selection, transforms and shared commands.
 * All mutations go through core transactions.
 */
export class Editor {
  readonly doc: CanvasDocument;
  readonly history: History;
  readonly scene: Scene;
  readonly renderer: Renderer;
  settings: EditorSettings;
  private host: EditorHost;
  private sel = new Set<Id>();
  private hoverId: Id | null = null;
  private tool: ToolDef = CORE_TOOLS[0];
  private tools = new Map<string, ToolDef>(CORE_TOOLS.map((t) => [t.id, t]));
  private drag: Drag | null = null;
  private spaceDown = false;
  /** Ctrl (⌘ on macOS) is held: the next drag of a selection copies it. */
  private copyArmed = false;
  private listeners: { [K in keyof EventMap]?: Set<(v: EventMap[K]) => void> } = {};
  private el: HTMLElement | null = null;
  private detachFns: (() => void)[] = [];
  private lastPointer: Vec2 = { x: 0, y: 0 };
  private editingId: Id | null = null;
  /** Box at the start of text editing, so a live grow is one undo step with the text. */
  private textBoxOrigin: { id: Id; x: number; y: number; w: number; h: number } | null = null;
  private pasteCount = 0;
  private unsub: (() => void)[] = [];
  /** Jumps of the camera (fit, bookmark, search). Pan and wheel zoom are not entries. */
  private views = new ViewTrail();
  /** View at the start of a minimap press; committed when the press ends. */
  private viewGesture: ViewSnap | null = null;
  /** Bumped when a camera flight must stop (back, forward, a newer flight). */
  viewEpoch = 0;
  /** Connector being hovered as a bind target while drawing connectors. */
  private bindHover: Id | null = null;

  constructor(doc: CanvasDocument, history: History, scene: Scene, renderer: Renderer, host: EditorHost, settings?: Partial<EditorSettings>) {
    this.doc = doc;
    this.history = history;
    this.scene = scene;
    this.renderer = renderer;
    this.host = host;
    this.settings = { ...DEFAULT_SETTINGS, ...settings };
    renderer.setScene(scene);
    renderer.setGrid(this.settings.grid);
    renderer.setOverlay((ctx, camera) => drawOverlay(ctx, camera, this));
    this.unsub.push(
      history.onChange(() => this.emit('history', undefined)),
      doc.onChange((e) => {
        let changed = false;
        for (const id of e.deleted) if (this.sel.delete(id)) changed = true;
        if (this.hoverId && e.deleted.includes(this.hoverId)) this.setHover(null);
        if (this.knobLatch && e.deleted.includes(this.knobLatch)) this.knobLatch = null;
        if (this.knobHover && e.deleted.includes(this.knobHover)) this.knobHover = null;
        if (changed) this.emit('selection', this.selection);
        this.renderer.invalidate();
      }),
    );
  }

  dispose(): void {
    this.stopZoomAnim();
    this.cancelDrag();
    for (const f of this.detachFns) f();
    for (const f of this.unsub) f();
    this.detachFns = [];
    this.renderer.setOverlay(null);
  }

  // ---------- events ----------
  on<K extends keyof EventMap>(ev: K, fn: (v: EventMap[K]) => void): () => void {
    let set = this.listeners[ev] as Set<(v: EventMap[K]) => void> | undefined;
    if (!set) this.listeners[ev] = set = new Set() as never;
    set!.add(fn);
    return () => set!.delete(fn);
  }
  private emit<K extends keyof EventMap>(ev: K, v: EventMap[K]): void {
    for (const fn of (this.listeners[ev] as Set<(v: EventMap[K]) => void> | undefined) ?? []) fn(v);
  }

  // ---------- state accessors (used by overlay and UI) ----------
  get selection(): Id[] {
    return [...this.sel];
  }
  get hover(): Id | null {
    return this.hoverId;
  }
  private setHover(id: Id | null): void {
    if (id === this.hoverId) return;
    this.hoverId = id;
    this.emit('hover', id);
  }
  get currentTool(): ToolDef {
    return this.tool;
  }
  get dragState(): Readonly<Drag> | null {
    return this.drag;
  }
  get readOnly(): boolean {
    return this.doc.readOnly;
  }
  get canViewBack(): boolean {
    return VIEW_HISTORY_ON && this.views.canBack;
  }
  get canViewForward(): boolean {
    return VIEW_HISTORY_ON && this.views.canForward;
  }
  get editing(): Id | null {
    return this.editingId;
  }
  get bindTarget(): Id | null {
    return this.bindHover;
  }
  get toolList(): ToolDef[] {
    return [...this.tools.values()];
  }

  caps(o: ObjectRecord): TypeCaps {
    if (o.locked) return { move: false, resize: false, rotate: false, editText: false };
    switch (o.type) {
      case CoreTypes.line:
      case CoreTypes.connector:
        return { move: o.type === CoreTypes.line, resize: false, rotate: false, editText: true };
      case CoreTypes.group:
        return { move: true, resize: true, rotate: false, editText: false };
      case CoreTypes.image:
      case CoreTypes.pen:
        return { move: true, resize: true, rotate: true, editText: false };
      case CoreTypes.frame:
        return { move: true, resize: true, rotate: false, editText: true };
      case CoreTypes.diagram:
        // edited as code in its own window; scaled as a whole
        return { move: true, resize: true, rotate: false, editText: false };
    }
    if (o.type.startsWith('core:')) return { move: true, resize: true, rotate: true, editText: true };
    const m = this.host.typeCaps?.(o.type);
    return { move: m?.move ?? true, resize: m?.resize ?? true, rotate: m?.rotate ?? false, editText: m?.editText ?? false };
  }

  /** Box used for selection chrome: single object box (rotated), or union for multi. */
  selectionBox(): Box | null {
    const ids = this.selection;
    if (ids.length === 0) return null;
    if (ids.length === 1) {
      const o = this.doc.get(ids[0]);
      if (!o) return null;
      if (o.type === CoreTypes.line || o.type === CoreTypes.connector) {
        const b = boxBounds(o);
        return { ...b, rotation: 0 };
      }
      return { x: o.x, y: o.y, w: o.w, h: o.h, rotation: o.rotation };
    }
    const r = cmd.selectionBounds(this.doc, ids);
    return r ? { ...r, rotation: 0 } : null;
  }

  selectionHandles(): HandleInfo[] {
    if (this.readOnly) return [];
    const box = this.selectionBox();
    if (!box) return [];
    const objs = this.selection.map((id) => this.doc.get(id)!).filter(Boolean);
    if (objs.length === 1 && (objs[0].type === CoreTypes.connector || objs[0].type === CoreTypes.line)) {
      // connector/line: handles on both ends to re-attach them
      const ends = objs[0].locked ? null : this.lineEnds(objs[0]);
      const cam = this.renderer.camera;
      return ends ? [{ id: 'p0', screen: cam.worldToScreen(ends[0].x, ends[0].y) }, { id: 'p1', screen: cam.worldToScreen(ends[1].x, ends[1].y) }] : [];
    }
    const caps = objs.map((o) => this.caps(o));
    const resize = caps.every((c) => c.resize);
    const rotate = caps.every((c) => c.rotate) && (objs.length > 1 || objs[0].type !== CoreTypes.group);
    return handlesFor(box, this.renderer.camera, { resize, rotate });
  }

  // ---------- selection ----------
  select(ids: Iterable<Id>, mode: 'replace' | 'add' | 'toggle' | 'remove' = 'replace'): void {
    const roots = [...ids].map((id) => cmd.topAncestor(this.doc, id)).filter((id) => this.doc.has(id));
    if (mode === 'replace') this.sel = new Set(roots);
    else
      for (const id of roots) {
        if (mode === 'add') this.sel.add(id);
        else if (mode === 'remove') this.sel.delete(id);
        else if (this.sel.has(id)) this.sel.delete(id);
        else this.sel.add(id);
      }
    if (this.pathEditId && !(this.sel.size === 1 && this.sel.has(this.pathEditId))) this.exitPathEdit();
    this.emit('selection', this.selection);
    this.renderer.invalidate();
  }

  clearSelection(): void {
    if (this.pathEditId) this.exitPathEdit();
    if (this.sel.size === 0) return;
    this.sel.clear();
    this.emit('selection', []);
    this.renderer.invalidate();
  }

  selectAll(): void {
    const roots = new Set<Id>();
    for (const o of this.doc.allObjects()) if (!o.parentId && !this.scene.isFolded(o.id)) roots.add(o.id);
    this.sel = roots;
    this.emit('selection', this.selection);
    this.renderer.invalidate();
  }

  // ---------- tools ----------
  registerTool(def: ToolDef): () => void {
    this.tools.set(def.id, def);
    this.emit('tool', this.tool.id);
    return () => {
      this.tools.delete(def.id);
      if (this.tool.id === def.id) this.setTool('select');
      else this.emit('tool', this.tool.id);
    };
  }

  setTool(id: string): void {
    const t = this.tools.get(id);
    if (!t) return;
    if (this.readOnly && t.kind !== 'select' && t.kind !== 'hand') return;
    this.cancelDrag();
    if (this.pathDraft && t.kind !== 'path') this.finishPath(false, false);
    if (this.pathEditId && t.kind !== 'select') this.exitPathEdit();
    this.tool = t;
    this.emit('tool', id);
    this.updateCursor();
    this.renderer.invalidate();
  }

  setSettings(patch: Partial<EditorSettings>): void {
    this.settings = { ...this.settings, ...patch };
    this.renderer.setGrid(this.settings.grid);
    this.emit('settings', this.settings);
  }

  // ---------- transactions helper ----------
  /** Run an edit as one undoable step, mapping known errors to user messages. */
  /** `module` (API 4.0): the edit is made for that module — its transaction, and the objects it adds are the module's own. */
  edit<T>(label: string, fn: (tx: TxContext) => T, module?: string): T | undefined {
    if (this.readOnly) {
      this.host.notify('warning', this.host.t('error.readOnly'));
      return undefined;
    }
    try {
      return this.doc.transact((tx) => {
        const by = this.host.objectAuthor?.();
        if (!by && !module) return fn(tx);
        // every object this edit adds belongs to the profile that is working now (and to the module it is made for)
        const orig = tx.create.bind(tx);
        tx.create = (rec) => orig({ ...rec, ...(by ? { addedBy: by } : {}), ...(module ? { createdByModule: module } : {}) });
        try {
          return fn(tx);
        } finally {
          tx.create = orig;
        }
      }, { label, source: module ? { kind: 'module', id: module } : { kind: 'user' } }).result;
    } catch (e) {
      this.reportError(e);
      return undefined;
    }
  }

  private reportError(e: unknown): void {
    if (e instanceof CoordinateLimitError) this.host.notify('warning', this.host.t('error.coordLimit'));
    else if (e instanceof ReadOnlyError) this.host.notify('warning', this.host.t('error.readOnly'));
    else if (e instanceof UndoBlockedError)
      this.host.notify('warning', this.host.t(e.reason === 'unavailable' ? 'error.undoBlocked' : 'error.undoIncompatible', { module: e.moduleId }));
    else if (e instanceof Error && 'code' in e && (e as { code: string }).code === 'E_CONFLICT') this.host.notify('warning', e.message);
    else if (e instanceof Error && 'code' in e && (e as { code: string }).code === 'E_CYCLE') this.host.notify('warning', this.host.t('error.cycle'));
    else {
      this.host.notify('error', e instanceof Error ? e.message : String(e));
      console.error(e);
    }
  }

  private checkConflict(ids: Iterable<Id>, op: 'move' | 'resize' | 'rotate' | 'delete' | 'style'): boolean {
    if (!this.host.conflict) return true;
    for (const id of ids) {
      const o = this.doc.get(id);
      if (!o) continue;
      const msg = this.host.conflict(o, op);
      if (msg) {
        this.host.notify('warning', msg);
        return false;
      }
    }
    return true;
  }

  // ---------- commands ----------
  undo(): void {
    try {
      this.history.undo();
    } catch (e) {
      this.reportError(e);
    }
  }
  redo(): void {
    try {
      this.history.redo();
    } catch (e) {
      this.reportError(e);
    }
  }

  deleteSelection(): void {
    const ids = this.selection.filter((id) => !this.doc.get(id)?.locked);
    if (!ids.length || !this.checkConflict(cmd.withContents(this.doc, ids), 'delete')) return;
    this.edit('delete', (tx) => {
      // connectors detouring around the removed shapes can take the shorter way again
      const near = this.crossingConnectors(new Set(cmd.withContents(this.doc, ids)));
      cmd.deleteDeep(tx, ids);
      for (const c of near) {
        const cur = this.doc.get(c.id);
        if (cur) rerouteConnector(tx, cur, (r) => this.scene.query(r));
      }
    });
  }

  /** Select the next (or previous) object in layer order; with nothing selected, the first (last). */
  selectNeighbor(dir: 1 | -1): void {
    const all = [...this.doc.allObjects()].filter((o) => o.type !== CoreTypes.group && !o.hidden && !this.scene.isFolded(o.id)).sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0));
    if (!all.length) return;
    const cur = this.selection.length === 1 ? all.findIndex((o) => o.id === this.selection[0]) : -1;
    const next = cur < 0 ? (dir === 1 ? 0 : all.length - 1) : (cur + dir + all.length) % all.length;
    this.select([all[next].id]);
    // keep it in view
    const o = all[next];
    const cam = this.renderer.camera;
    const r = cam.visibleRect();
    if (o.x + o.w < r.x || o.y + o.h < r.y || o.x > r.x + r.w || o.y > r.y + r.h) this.fitSelection();
  }

  /** Selected objects a shape operation can use (outlines, not locked), from the back to the front. */
  booleanTargets(): ObjectRecord[] {
    return this.selection
      .map((id) => this.doc.get(id))
      .filter((o): o is ObjectRecord => !!o && !o.locked && booleanSupported(o))
      .sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0));
  }

  canBoolean(): boolean {
    return !this.readOnly && this.booleanTargets().length >= 2;
  }

  /**
   * Combine overlapping shapes into new closed paths: union, subtract (the ones in front cut out of
   * the one at the back), intersect, exclude (where an odd number overlap) or divide (every region
   * becomes its own piece). The result takes the look of the back shape; one undo step.
   */
  booleanOp(op: BooleanOp): void {
    const targets = this.booleanTargets();
    if (this.readOnly || targets.length < 2) return;
    if (!this.checkConflict(targets.map((o) => o.id), 'delete')) return;
    const polys = targets.map((o) => objectPolygon(o));
    if (polys.some((q) => !q)) return;
    let pieces;
    try {
      pieces = piecesOf(booleanPolygons(op, polys as NonNullable<(typeof polys)[number]>[]));
    } catch (e) {
      this.reportError(e);
      this.host.notify('warning', this.host.t('boolean.failed'));
      return;
    }
    if (!pieces.length) {
      this.host.notify('info', this.host.t('boolean.empty'));
      return;
    }
    const back = targets[0];
    const top = targets[targets.length - 1];
    // right above the front shape, so the result keeps its place among the other objects
    let above: string | null = null;
    for (const o of this.doc.allObjects()) if (o.z > top.z && (above === null || o.z < above)) above = o.z;
    const zs = keysBetween(top.z, above, pieces.length);
    const ids = this.edit('boolean', (tx) => {
      const created: Id[] = [];
      cmd.deleteDeep(tx, targets.map((o) => o.id));
      pieces.forEach((pc, i) => {
        const rec = makeObject(this.doc.canvasId, {
          type: CoreTypes.path,
          x: pc.x,
          y: pc.y,
          w: pc.w,
          h: pc.h,
          z: zs[i],
          style: resultStyle(back),
          props: { nodes: pc.nodes, points: pc.points, closed: true, ...(pc.holes.length ? { holes: pc.holes } : {}) },
        });
        tx.create(back.frameId ? { ...rec, frameId: back.frameId } : rec);
        created.push(rec.id);
      });
      return created;
    });
    if (ids) this.select(ids);
  }

  duplicateSelection(): void {
    const ids = this.selection;
    if (!ids.length) return;
    const roots = this.edit('duplicate', (tx) => {
      const r = cmd.duplicate(tx, ids, 20);
      cmd.assignFrames(tx, r);
      return r;
    });
    if (roots) this.select(roots);
  }

  groupSelection(): void {
    const g = this.edit('group', (tx) => cmd.group(tx, this.selection));
    if (g) this.select([g]);
  }

  ungroupSelection(): void {
    const groups = this.selection.filter((id) => this.doc.get(id)?.type === CoreTypes.group);
    if (!groups.length) return;
    const freed = this.edit('ungroup', (tx) => cmd.ungroup(tx, groups));
    if (freed) this.select(freed);
  }

  reorderSelection(mode: 'front' | 'back' | 'forward' | 'backward'): void {
    if (!this.sel.size) return;
    this.edit('reorder', (tx) => {
      cmd.reorder(tx, this.selection, mode);
      // contents never go below their frame
      for (const id of this.selection) {
        const f = this.doc.get(id)?.frameId;
        if (f) cmd.ensureAboveFrame(tx, id, f);
      }
    });
  }

  alignSelection(mode: Parameters<typeof cmd.align>[2]): void {
    const ids = this.selection;
    if (!this.checkConflict(cmd.withContents(this.doc, ids), 'move')) return;
    this.edit('align', (tx) => {
      cmd.align(tx, ids, mode);
      cmd.assignFrames(tx, ids);
      this.rerouteFor(tx, new Set(cmd.withContents(this.doc, ids)));
    });
  }

  toggleLock(): void {
    const ids = this.selection;
    if (!ids.length) return;
    const lock = !ids.every((id) => this.doc.get(id)?.locked);
    this.edit(lock ? 'lock' : 'unlock', (tx) => cmd.setLocked(tx, ids, lock));
  }

  nudge(dx: number, dy: number): void {
    const ids = this.selection.filter((id) => {
      const o = this.doc.get(id);
      return o && this.caps(o).move;
    });
    if (!ids.length || !this.checkConflict(ids, 'move')) return;
    const folded = this.scene.foldedUnder(cmd.withContents(this.doc, ids));
    this.edit('move', (tx) => {
      cmd.moveBy(tx, [...ids, ...folded], dx, dy);
      cmd.assignFrames(tx, ids);
      this.rerouteFor(tx, new Set(cmd.withContents(this.doc, [...ids, ...folded])));
    });
  }

  /** Apply a style patch to selected objects (and group members). */
  setStyle(patch: Partial<Style>): void {
    this.updateStyles(() => patch);
  }

  /**
   * Style change computed per selected leaf object; null skips the object, so a mixed
   * selection only changes what applies to each member (fill never lands on a line, etc.).
   */
  updateStyles(fn: (o: ObjectRecord) => Partial<Style> | null): void {
    const updates: [Id, Partial<Style>][] = [];
    for (const id of cmd.withDescendants(this.doc, this.selection)) {
      const o = this.doc.get(id);
      if (!o || o.type === CoreTypes.group) continue;
      const patch = fn(o);
      if (patch) updates.push([id, patch]);
    }
    if (!updates.length || !this.checkConflict(updates.map((u) => u[0]), 'style')) return;
    this.edit('style', (tx) => {
      for (const [id, patch] of updates) {
        tx.updateStyle(id, patch as Record<string, unknown>);
        // a bigger or bolder font may no longer fit: the shape grows with it
        if (patch.text) this.fitTextBox(tx, id);
      }
    });
  }

  setProps(ids: Id[], props: Record<string, unknown>, label = 'props'): void {
    this.edit(label, (tx) => {
      for (const id of ids) if (this.doc.has(id)) tx.updateProps(id, props);
    });
  }

  updateObjects(updates: { id: Id; patch: Partial<ObjectRecord> }[], label = 'update'): void {
    this.edit(label, (tx) => {
      const changed = new Set<Id>();
      for (const u of updates) {
        if (!this.doc.has(u.id)) continue;
        tx.update(u.id, u.patch);
        changed.add(u.id);
        const p = this.doc.get(u.id)?.parentId;
        if (p) cmd.updateGroupBounds(tx, p);
      }
      this.rerouteFor(tx, changed);
    });
  }

  zoomBy(factor: number): void {
    this.stopZoomAnim();
    const c = this.renderer.camera;
    c.zoomAt(c.width / 2, c.height / 2, factor);
  }

  zoomTo(z: number): void {
    this.reveal(() => {
      const c = this.renderer.camera;
      c.zoomAt(c.width / 2, c.height / 2, z / c.zoom);
    });
  }

  /** `record: false` is the automatic fit when a canvas opens — the user never chose that view. */
  fitAll(opts?: { record?: boolean }): void {
    const apply = (): void => {
      const b = this.scene.index.totalBounds();
      if (b) this.renderer.camera.fit(b, 64, this.naturalZoom(this.doc.allObjects()));
      else this.renderer.camera.set({ cx: 0, cy: 0, zoom: 1 });
    };
    if (opts?.record === false) {
      this.stopZoomAnim();
      apply();
    } else this.reveal(apply);
  }

  fitSelection(): void {
    this.reveal(() => {
      const r = cmd.selectionBounds(this.doc, this.selection);
      const objs = this.selection.map((id) => this.doc.get(id)).filter((o): o is ObjectRecord => !!o);
      if (r) this.renderer.camera.fit(r, 96, this.naturalZoom(objs));
    });
  }

  /**
   * Size an object to its live view (manifest `view.fit: grow`): not a step of its own in the
   * history — the view follows its content, undoing the content brings the size back with it.
   */
  fitToView(id: Id, size: { w?: number; h?: number }): void {
    const o = this.doc.get(id);
    if (!o || this.readOnly || o.locked) return;
    const after: Record<string, number> = {};
    const before: Record<string, number> = {};
    if (size.w !== undefined && Math.abs(size.w - o.w) > 0.5) {
      after['w'] = size.w;
      before['w'] = o.w;
    }
    if (size.h !== undefined && Math.abs(size.h - o.h) > 0.5) {
      after['h'] = size.h;
      before['h'] = o.h;
    }
    if (!Object.keys(after).length) return;
    try {
      this.doc.applyOps([{ t: 'update', table: 'objects', id, before, after }], { source: { kind: 'system' }, label: 'view.fit' }, { history: true });
    } catch (e) {
      this.reportError(e);
    }
  }

  /** Show given objects (the camera fits them; selection is not changed). */
  fitObjects(ids: Id[]): void {
    this.reveal(() => {
      const r = cmd.selectionBounds(this.doc, ids);
      const objs = ids.map((id) => this.doc.get(id)).filter((o): o is ObjectRecord => !!o);
      if (r) this.renderer.camera.fit(r, 96, this.naturalZoom(objs));
    });
  }

  private camSnap(): ViewSnap {
    const c = this.renderer.camera;
    return { cx: c.cx, cy: c.cy, zoom: c.zoom };
  }

  /**
   * Apply a camera jump and remember the previous view when it actually moves.
   * Wheel zoom and dragging the canvas do not go through here.
   */
  reveal(apply: () => void): void {
    this.viewEpoch++;
    this.stopZoomAnim();
    this.endViewGesture();
    const from = this.camSnap();
    apply();
    if (VIEW_HISTORY_ON && this.views.note(from, this.camSnap())) this.emit('view', undefined);
  }

  /**
   * The camera is about to travel to `to` over time (a bookmark, a link).
   * Returns the epoch the animation must keep; a newer jump invalidates it.
   */
  beginFlight(to: ViewSnap): number {
    this.viewEpoch++;
    this.stopZoomAnim();
    this.endViewGesture();
    if (VIEW_HISTORY_ON && this.views.note(this.camSnap(), to)) this.emit('view', undefined);
    return this.viewEpoch;
  }

  /** One history entry for a whole minimap press, including the drag. */
  beginViewGesture(): void {
    if (this.viewGesture) return;
    this.viewGesture = this.camSnap();
  }

  endViewGesture(): void {
    const from = this.viewGesture;
    this.viewGesture = null;
    if (VIEW_HISTORY_ON && from && this.views.note(from, this.camSnap())) this.emit('view', undefined);
  }

  backView(): boolean {
    if (!VIEW_HISTORY_ON) return false;
    this.endViewGesture();
    const prev = this.views.retreat(this.camSnap());
    if (!prev) return false;
    this.viewEpoch++;
    this.stopZoomAnim();
    this.renderer.camera.set(prev);
    this.emit('view', undefined);
    return true;
  }

  forwardView(): boolean {
    if (!VIEW_HISTORY_ON) return false;
    this.endViewGesture();
    const next = this.views.advance(this.camSnap());
    if (!next) return false;
    this.viewEpoch++;
    this.stopZoomAnim();
    this.renderer.camera.set(next);
    this.emit('view', undefined);
    return true;
  }

  /** An arrow from one object to another, glued to both and routed like one drawn by hand. */
  connectObjects(from: Id, to: Id, opts: { label?: string; arrow?: boolean; module?: string } = {}): Id | undefined {
    const a = this.doc.get(from);
    const b = this.doc.get(to);
    if (!a || !b || this.readOnly) return undefined;
    const center = (o: ObjectRecord) => ({ x: o.x + o.w / 2, y: o.y + o.h / 2 });
    return this.edit('create', (tx) => this.connectorIn(tx, { objectId: from, point: center(a) }, { objectId: to, point: center(b) }, { arrowEnd: opts.arrow ?? true, label: opts.label }), opts.module);
  }

  /**
   * The zoom at which content looks its intended size: objects are created scaled to the
   * zoom of the moment (see viewScale), so fitting never magnifies beyond the zoom they were made at.
   */
  private naturalZoom(objs: Iterable<ObjectRecord>): number {
    const ks: number[] = [];
    for (const o of objs) {
      const base = defaultStyle(o.type);
      if (o.style.text && base.text) ks.push(o.style.text.fontSize / base.text.fontSize);
      else if (o.style.stroke && base.stroke) ks.push(o.style.stroke.width / base.stroke.width);
      if (ks.length >= 4000) break;
    }
    if (!ks.length) return 1;
    ks.sort((x, y) => x - y);
    const k = ks[ks.length >> 1];
    return k > 0 && Number.isFinite(k) ? 1 / k : 1;
  }

  /**
   * World units per screen pixel at the current zoom, used so that new objects,
   * fonts and strokes look the same on screen whatever the zoom (close to 1 → exactly 1).
   */
  get viewScale(): number {
    const k = 1 / this.renderer.camera.zoom;
    if (Math.abs(Math.log2(k)) < 0.15) return 1;
    return parseFloat(k.toPrecision(2));
  }

  /**
   * Style of new objects of a core type: the user's default style for it (if one was made) over the
   * built-in one, scaled to the current zoom (fonts, strokes, corner radius).
   */
  scaledStyle(type: string): Partial<Style> | undefined {
    const k = this.viewScale;
    const user = this.settings.defaultStyles?.[type];
    if (!type.startsWith('core:')) return undefined;
    if (k === 1 && !user) return undefined;
    const base: Partial<Style> = { ...defaultStyle(type), ...(user ?? {}) };
    if (user?.text) base.text = { ...defaultStyle(type).text!, ...user.text };
    return scaleStyle(base, k);
  }

  /** A tool's own style (a capsule, a node…) scaled to the current zoom, like the type's style under it. */
  private toolStyle(tool: ToolDef): Partial<Style> | undefined {
    return tool.style && scaleStyle(tool.style, this.viewScale);
  }

  /** The user's default style of a module type, scaled to the current zoom. */
  private userModuleStyle(type: string): Partial<Style> | undefined {
    const user = this.settings.defaultStyles?.[type];
    return user ? scaleStyle(user, this.viewScale) : undefined;
  }

  /** Make the style of this object the style of new objects of its type (null: back to the built-in one). */
  setDefaultStyle(type: string, style: Partial<Style> | null): void {
    const next = { ...(this.settings.defaultStyles ?? {}) };
    if (style) next[type] = scaleStyle(pickStyle(style), 1 / this.viewScale);
    else delete next[type];
    this.setSettings({ defaultStyles: next });
  }

  /** Keep the width-to-height ratio of the selected objects when they are resized. */
  setKeepRatio(on: boolean): void {
    const ids = this.selection.filter((id) => this.doc.get(id)?.type !== CoreTypes.diagram);
    if (!ids.length || this.readOnly) return;
    this.updateObjects(
      ids.map((id) => ({ id, patch: { keepRatio: on || undefined } as Partial<ObjectRecord> })),
      'keep-ratio',
    );
  }

  /** Create an object of a type at a world rect; returns id. */
  async createObject(type: string, rect: Rect, extra: { props?: Record<string, unknown>; style?: Partial<Style> } = {}): Promise<Id | undefined> {
    let props = extra.props;
    if (!type.startsWith('core:') && this.host.createDefaults) {
      try {
        props = { ...(await this.host.createDefaults(type, { w: rect.w, h: rect.h })), ...(props ?? {}) };
      } catch (e) {
        this.reportError(e);
        return undefined;
      }
    }
    const id = this.edit('create', (tx) => {
      const formatVersion = type.startsWith('core:') ? undefined : this.host.typeFormatVersion?.(type);
      const mt = type.startsWith('core:') ? undefined : this.host.typeText?.(type);
      const ms = type.startsWith('core:') ? undefined : this.host.typeStyle?.(type);
      const us = type.startsWith('core:') ? undefined : this.userModuleStyle(type);
      const style = { ...(mt ? { text: { ...mt.style, fontSize: mt.style.fontSize * this.viewScale } } : {}), ...(this.scaledStyle(type) ?? {}), ...(ms ?? {}), ...(us ?? {}), ...(extra.style ?? {}) };
      const o = makeObject(this.doc.canvasId, { type, ...rect, z: cmd.nextZ(this.doc), props, style, formatVersion });
      tx.create(o);
      // frames are containers: a new frame takes what it covers, other objects join the frame under them
      if (type === CoreTypes.frame) cmd.adoptIntoFrame(tx, o.id);
      cmd.assignFrames(tx, [o.id]);
      this.rerouteFor(tx, new Set([o.id]));
      return o.id;
    });
    if (id) this.select([id]);
    return id;
  }

  // ---------- clipboard (§4.6) ----------
  copyPayload(): ClipPayload | null {
    return cmd.serializeSelection(this.doc, this.selection);
  }

  cutPayload(): ClipPayload | null {
    const p = this.copyPayload();
    if (p) this.deleteSelection();
    return p;
  }

  /** Paste at cursor (if over canvas) or view center, with offset for repeated pastes. */
  paste(payload: ClipPayload, at?: Vec2): Id[] {
    const cam = this.renderer.camera;
    let target = at;
    if (!target) {
      const p = this.lastPointer;
      const inside = p.x > 0 && p.y > 0 && p.x < cam.width && p.y < cam.height;
      const w = inside ? cam.screenToWorld(p.x, p.y) : { x: cam.cx, y: cam.cy };
      target = { x: w.x - payload.bounds.w / 2, y: w.y - payload.bounds.h / 2 };
    }
    const off = this.pasteCount++ * 20;
    const roots = this.edit('paste', (tx) => {
      const r = cmd.instantiatePayload(tx, payload, { x: target!.x + off, y: target!.y + off });
      cmd.assignFrames(tx, r);
      return r;
    });
    if (roots) this.select(roots);
    return roots ?? [];
  }

  resetPasteOffset(): void {
    this.pasteCount = 0;
  }

  async pasteText(text: string): Promise<void> {
    const c = this.pointerWorldOrCenter();
    if (looksLikeDskhema(text)) {
      this.createDiagram(text, c);
      return;
    }
    const k = this.viewScale;
    const textStyle = this.scaledStyle(CoreTypes.text)?.text ?? defaultStyle(CoreTypes.text).text!;
    const w = Math.min(600, Math.max(120, text.length * 9)) * k;
    const h = measureTextHeight(text, textStyle, w);
    await this.createObject(CoreTypes.text, { x: c.x - w / 2, y: c.y - h / 2, w, h }, { props: { text } });
  }

  async pasteImage(attachment: string, width: number, height: number): Promise<void> {
    const c = this.pointerWorldOrCenter();
    // fit into ~800 screen px at the current zoom
    const k = Math.min(1, 800 / Math.max(width, height)) * this.viewScale;
    const w = width * k;
    const h = height * k;
    await this.createObject(CoreTypes.image, { x: c.x - w / 2, y: c.y - h / 2, w, h }, { props: { attachment } });
  }

  pointerWorldOrCenter(): Vec2 {
    const cam = this.renderer.camera;
    const p = this.lastPointer;
    const inside = p.x > 0 && p.y > 0 && p.x < cam.width && p.y < cam.height;
    return inside ? cam.screenToWorld(p.x, p.y) : { x: cam.cx, y: cam.cy };
  }

  // ---------- text editing ----------
  startTextEdit(id: Id): void {
    const o = this.doc.get(id);
    if (!o || this.readOnly || !this.caps(o).editText) return;
    this.editingId = id;
    this.textBoxOrigin = { id, x: o.x, y: o.y, w: o.w, h: o.h };
    this.scene.hiddenText = id;
    this.scene.invalidateObjects([id]);
    this.emit('textedit', id);
  }

  /** While an arrow's label is typed: the line breaks around the typed text right away. */
  typeLabel(id: Id, text: string): void {
    if (this.editingId !== id) return;
    const o = this.doc.get(id);
    if (!o || (o.type !== CoreTypes.connector && o.type !== CoreTypes.line)) return;
    this.scene.draftLabel = { id, text };
    this.scene.invalidateObjects([id]);
  }

  /**
   * While typing, grow a text object's frame to the editor (top-left stays put).
   * Not an undo step of its own: finishing or cancelling folds the size into one step.
   */
  growTextFrame(id: Id, h: number): void {
    if (this.editingId !== id || !Number.isFinite(h)) return;
    const o = this.doc.get(id);
    if (!o || o.locked || o.type !== CoreTypes.text) return;
    h = Math.max(o.style.text ? o.style.text.fontSize * 1.6 : 8, h);
    if (Math.abs(h - o.h) < 0.5) return;
    const dh = (h - o.h) / 2;
    const c = Math.cos(o.rotation);
    const sn = Math.sin(o.rotation);
    const cx = o.x + o.w / 2 - dh * sn;
    const cy = o.y + o.h / 2 + dh * c;
    this.quietGeometry(id, { x: cx - o.w / 2, y: cy - h / 2, w: o.w, h });
  }

  /** Geometry change that the undo stack does not record. */
  private quietGeometry(id: Id, patch: { x: number; y: number; w: number; h: number }): void {
    const ctx = this.doc.begin();
    try {
      ctx.update(id, patch);
      const parent = this.doc.get(id)?.parentId;
      if (parent) cmd.updateGroupBounds(ctx, parent);
      this.rerouteFor(ctx, new Set([id]));
      ctx.commit({ history: true });
    } catch (e) {
      if (ctx.isOpen) ctx.rollback();
      throw e;
    }
  }

  /** Put the edited text object back to the box it had when editing started. */
  private restoreTextBox(): void {
    const b = this.textBoxOrigin;
    this.textBoxOrigin = null;
    if (!b) return;
    const o = this.doc.get(b.id);
    if (!o) return;
    if (Math.abs(o.x - b.x) < 0.01 && Math.abs(o.y - b.y) < 0.01 && Math.abs(o.w - b.w) < 0.01 && Math.abs(o.h - b.h) < 0.01) return;
    this.quietGeometry(b.id, b);
  }

  /**
   * Commit edited text; grows text objects to fit (§5.5). `spans` — formatted fragments of the text
   * (shapes, stickers, text objects); null or omitted keeps it plain.
   */
  commitText(id: Id, text: string, spans?: TextSpan[] | null): void {
    const o = this.doc.get(id);
    this.editingId = null;
    // the live grow is not on the undo stack; the text step below records the final box
    this.restoreTextBox();
    this.scene.hiddenText = null;
    this.scene.draftLabel = null;
    this.scene.invalidateObjects([id]);
    this.emit('textedit', null);
    if (!o) return;
    const key = o.type === CoreTypes.frame ? 'title' : o.type === CoreTypes.connector || o.type === CoreTypes.line ? 'label' : 'text';
    const rich = key === 'text' && spans && spans.length ? normalizeSpans(spans) : null;
    const nextSpans = rich && hasFormatting(rich) && spansText(rich) === text ? rich : undefined;
    const prevSpans = key === 'text' ? readSpans(text, o.props['spans']) ?? undefined : undefined;
    if ((o.props[key] ?? '') === text && JSON.stringify(prevSpans) === JSON.stringify(nextSpans) && o.type !== CoreTypes.text) return;
    if (o.type === CoreTypes.text && text.trim() === '') {
      this.edit('delete', (tx) => tx.delete(id));
      return;
    }
    this.edit('text', (tx) => {
      tx.updateProps(id, key === 'text' ? { text, spans: nextSpans } : { [key]: text });
      if (key === 'label' && text) {
        const pts = (o.type === CoreTypes.line ? o.props['points'] : o.props['path']) as number[] | undefined;
        if (Array.isArray(pts) && pts.length >= 4) {
          const base = o.style.text ?? DEFAULT_TEXT_STYLE;
          const fontSize = fitArrowLabel(text, base, pathLength(pts));
          if (Math.abs(fontSize - (o.style.text?.fontSize ?? 0)) > 0.05) tx.update(id, { style: { ...o.style, text: { ...base, fontSize } } });
        }
      }
      this.fitTextBox(tx, id);
    });
  }

  /**
   * Grow a shape so its text fits (never shrinks it; words are not broken across lines).
   * A text object keeps the width it was edited at, so the wrap shown while typing stays,
   * and only its height follows the lines. The top-left corner stays put, rotation included.
   */
  private fitTextBox(tx: TxContext, id: Id): void {
    const o0 = this.doc.get(id);
    if (!o0 || o0.locked) return;
    const mt = o0.type.startsWith('core:') ? undefined : this.host.typeText?.(o0.type);
    if (mt) {
      // module object with text: grows downwards to fit it when the type asks for that
      const text = typeof o0.props['text'] === 'string' ? (o0.props['text'] as string) : '';
      if (!mt.fit || !text) return;
      const st = o0.style.text ?? mt.style;
      const need = textBoxHeight(text, readSpans(text, o0.props['spans']), st, Math.max(1, o0.w - mt.inset.left - mt.inset.right)) + mt.inset.top + mt.inset.bottom;
      if (need <= o0.h + 0.01) return;
      const dh = (need - o0.h) / 2;
      tx.update(id, { x: o0.x - dh * Math.sin(o0.rotation), y: o0.y + dh * Math.cos(o0.rotation) - dh, h: need });
      if (o0.parentId) cmd.updateGroupBounds(tx, o0.parentId);
      this.rerouteFor(tx, new Set([id]));
      return;
    }
    const o = o0;
    // a flowchart form sizes its text to itself (formTextStyle)
    if (!o.style.text || rectForm(o)) return;
    const text = typeof o.props['text'] === 'string' ? (o.props['text'] as string) : '';
    const spans = readSpans(text, o.props['spans']);
    let w = o.w;
    let h = o.h;
    if (o.type === CoreTypes.text) {
      if (!text) return;
      h = textBoxHeight(text, spans, o.style.text, w);
    } else if (o.type === CoreTypes.rect || o.type === CoreTypes.sticker || o.type === CoreTypes.ellipse || o.type === CoreTypes.triangle || o.type === CoreTypes.diamond) {
      const fit = fitBoxToContent(text, spans, o.style.text, o.w, o.h);
      w = fit.w;
      h = fit.h;
      if (o.type === CoreTypes.sticker && (w > o.w + 0.01 || h > o.h + 0.01)) {
        // stickers stay square
        const side = Math.max(w, h);
        w = side;
        h = Math.max(side, fitBoxToContent(text, spans, o.style.text, side, side).h);
        w = Math.max(w, h);
        h = w;
      }
    } else return;
    if (Math.abs(w - o.w) < 0.01 && Math.abs(h - o.h) < 0.01) return;
    const dw = (w - o.w) / 2;
    const dh = (h - o.h) / 2;
    const c = Math.cos(o.rotation);
    const sn = Math.sin(o.rotation);
    const cx = o.x + o.w / 2 + dw * c - dh * sn;
    const cy = o.y + o.h / 2 + dw * sn + dh * c;
    tx.update(id, { x: cx - w / 2, y: cy - h / 2, w, h });
    const parent = o.parentId;
    if (parent) cmd.updateGroupBounds(tx, parent);
    this.rerouteFor(tx, new Set([id]));
  }

  cancelTextEdit(): void {
    const id = this.editingId;
    this.editingId = null;
    this.restoreTextBox();
    this.scene.hiddenText = null;
    this.scene.draftLabel = null;
    if (id) {
      this.scene.invalidateObjects([id]);
      const o = this.doc.get(id);
      if (o && o.type === CoreTypes.text && !(o.props['text'] as string)) this.edit('delete', (tx) => tx.delete(id));
    }
    this.emit('textedit', null);
  }

  // ---------- connectors ----------
  /** Re-route connectors attached to changed objects, cascading to connectors attached to those. */
  private rerouteFor(tx: TxContext, changed: Set<Id>): void {
    const obstacles = this.obstacleQuery(changed);
    let frontier = changed;
    const done = new Set<Id>();
    for (let depth = 0; depth < 8 && frontier.size; depth++) {
      const next = new Set<Id>();
      for (const c of connectorsAttachedTo(this.doc, frontier)) {
        if (done.has(c.id) || changed.has(c.id)) continue;
        done.add(c.id);
        rerouteConnector(tx, this.doc.get(c.id)!, obstacles);
        next.add(c.id);
      }
      frontier = next;
    }
    // connectors that merely pass where a changed object is (or was) must detour again
    for (const c of this.crossingConnectors(changed)) {
      if (done.has(c.id) || changed.has(c.id)) continue;
      done.add(c.id);
      rerouteConnector(tx, c, obstacles);
    }
  }

  /** Obstacle lookup that also sees objects changed in the open transaction (the index may lag behind them). */
  private obstacleQuery(extra: Set<Id>): (r: Rect) => Id[] {
    return (r) => {
      const ids = new Set(this.scene.query(r));
      for (const id of extra) {
        const o = this.doc.get(id);
        if (o && rectsOverlap(boxBounds(o), r)) ids.add(id);
      }
      return [...ids];
    };
  }

  /** Orthogonal connectors around the current or previous area of the changed shapes. */
  private crossingConnectors(changed: Set<Id>): ObjectRecord[] {
    const zones: Rect[] = [];
    for (const id of changed) {
      const o = this.doc.get(id);
      if (!o || isLineLike(o) || o.type === CoreTypes.frame || o.type === CoreTypes.group || o.type === CoreTypes.pen) continue;
      zones.push(boxBounds(o));
      const old = this.scene.index.bounds(id);
      if (old) zones.push(old);
      if (zones.length > 80) break;
    }
    if (!zones.length) return [];
    const out = new Map<Id, ObjectRecord>();
    for (const z of zones) {
      for (const cid of this.scene.query(z)) {
        if (out.has(cid) || changed.has(cid)) continue;
        // its box overlaps: it may cross the shape now, or detour around a place that is free again
        const c = this.doc.get(cid);
        if (c && c.type === CoreTypes.connector && c.props['route'] === 'orthogonal') out.set(cid, c);
      }
    }
    return [...out.values()];
  }

  // ---------- input ----------
  attach(el: HTMLElement): void {
    this.el = el;
    el.tabIndex = 0;
    const on = <K extends keyof HTMLElementEventMap>(type: K, fn: (e: HTMLElementEventMap[K]) => void, opts?: AddEventListenerOptions) => {
      el.addEventListener(type, fn as EventListener, opts);
      this.detachFns.push(() => el.removeEventListener(type, fn as EventListener, opts));
    };
    on('pointerdown', (e) => this.onPointerDown(e));
    on('pointermove', (e) => this.onPointerMove(e));
    on('pointerup', (e) => this.onPointerUp(e));
    on('pointercancel', () => this.cancelDrag());
    on('wheel', (e) => this.onWheel(e), { passive: false });
    on('dblclick', (e) => this.onDblClick(e));
    on('contextmenu', (e) => {
      e.preventDefault();
      // with the right button moving the canvas, the menu belongs to a click without a drag (opened on
      // release); the event of the same press is skipped. The Menu key and a long press still open it.
      if (this.settings.rightDragPan && performance.now() - this.rightSeqAt < 600) return;
      this.openMenuAt(e.clientX, e.clientY);
    });
    on('keydown', (e) => {
      this.noteCopyKey(e);
      this.onKeyDown(e);
    });
    on('keyup', (e) => {
      this.noteCopyKey(e);
      if (this.offerKey(e, 'up')) return;
      if (e.code === 'Space') {
        this.spaceDown = false;
        this.updateCursor();
      }
    });
    on('pointerleave', () => {
      const dirty = this.hoverId !== null || this.knobLatch !== null || this.knobHover !== null;
      this.setHover(null);
      this.knobLatch = null;
      this.knobHover = null;
      if (dirty) this.renderer.invalidate();
    });
  }

  private local(e: { clientX: number; clientY: number }): Vec2 {
    const r = this.el!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private setCursor(c: string): void {
    if (this.el && this.el.style.cursor !== c) {
      this.el.style.cursor = c;
      this.emit('cursor', c);
    }
  }

  private rightSeqAt = -1e9;

  private openMenuAt(clientX: number, clientY: number): void {
    const rect = this.el?.getBoundingClientRect();
    const p = { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) };
    const w = this.renderer.camera.screenToWorld(p.x, p.y);
    const hit = this.scene.hitTest(w, 4 / this.renderer.camera.zoom);
    if (hit && !this.sel.has(cmd.topAncestor(this.doc, hit))) this.select([hit]);
    if (!hit) this.clearSelection();
    this.emit('contextmenu', { x: clientX, y: clientY, target: hit ? cmd.topAncestor(this.doc, hit) : null });
  }

  private updateCursor(): void {
    if (this.drag?.kind === 'pan') this.setCursor('grabbing');
    else if (this.spaceDown || this.tool.kind === 'hand') this.setCursor('grab');
    else if (this.tool.kind === 'select') this.setCursor('default');
    else if (this.tool.kind === 'text') this.setCursor('text');
    else if (this.tool.kind === 'eraser') this.setCursor('none');
    else if (this.tool.kind === 'lasso') this.setCursor('crosshair');
    else this.setCursor('crosshair');
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const p = this.local(e);
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;
    else if (e.deltaMode === 2) dy *= 400;
    if (e.shiftKey && !e.ctrlKey) {
      this.renderer.camera.panBy(-dy, 0);
      return;
    }
    // pinch on trackpads arrives as ctrl+wheel with small, frequent deltas: apply directly
    if (e.ctrlKey) {
      this.stopZoomAnim();
      this.renderer.camera.zoomAt(p.x, p.y, Math.exp(-dy * 0.01));
      return;
    }
    // mouse wheel (§4.3): each notch moves a target zoom, the camera eases towards it
    const cam = this.renderer.camera;
    const base = this.zoomAnim?.target ?? cam.zoom;
    const target = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, base * Math.exp(-Math.max(-240, Math.min(240, dy)) * 0.0022)));
    if (this.zoomAnim) {
      this.zoomAnim.target = target;
      this.zoomAnim.x = p.x;
      this.zoomAnim.y = p.y;
      return;
    }
    this.zoomAnim = { target, x: p.x, y: p.y, raf: 0 };
    const stepAnim = () => {
      const a = this.zoomAnim;
      if (!a) return;
      const ratio = a.target / cam.zoom;
      if (Math.abs(Math.log(ratio)) < 0.002) {
        cam.zoomAt(a.x, a.y, ratio);
        this.zoomAnim = null;
        return;
      }
      // exponential easing in log space: ~120 ms to settle
      cam.zoomAt(a.x, a.y, Math.pow(ratio, 0.28));
      a.raf = requestAnimationFrame(stepAnim);
    };
    this.zoomAnim.raf = requestAnimationFrame(stepAnim);
  }

  private zoomAnim: { target: number; x: number; y: number; raf: number } | null = null;

  private stopZoomAnim(): void {
    if (this.zoomAnim) cancelAnimationFrame(this.zoomAnim.raf);
    this.zoomAnim = null;
  }

  private onDblClick(e: MouseEvent): void {
    if (this.readOnly) return;
    if (this.tool.kind === 'path') {
      // the second press of the double click added a node at the same spot: drop it, finish the open path
      if (this.pathDraft) this.pathUndoNode();
      this.finishPath(false);
      return;
    }
    const p = this.local(e);
    const w = this.renderer.camera.screenToWorld(p.x, p.y);
    const hit = this.scene.hitTest(w, 4 / this.renderer.camera.zoom);
    const hitObj = hit ? this.doc.get(hit) : undefined;
    if (this.pathEditId) return;
    if (hitObj?.type === CoreTypes.path && !hitObj.locked) {
      this.enterPathEdit(hitObj.id);
      return;
    }
    if (hit && hitObj && !(hitObj.type === CoreTypes.frame && worldToLocal(hitObj, w).y >= 0)) {
      const o = hitObj;
      if (this.caps(o).editText) {
        this.select([hit]);
        this.startTextEdit(hit);
      } else {
        // e.g. module objects with live views (mode C) become interactive
        this.select([hit]);
        this.emit('activate', hit);
      }
      return;
    }
    // empty canvas, or the inside of a frame: what happens is a setting
    if (this.tool.kind === 'select') {
      const mode = this.settings.emptyDblClick;
      if (mode === 'zoom') this.renderer.camera.zoomAt(p.x, p.y, 2);
      else if (mode === 'text') void this.createText(w);
    }
  }

  private async createText(w: Vec2): Promise<void> {
    const def = this.tools.get('text')!;
    const k = this.viewScale;
    const size = { w: parseFloat((def.defaultSize!.w * k).toPrecision(4)), h: parseFloat((def.defaultSize!.h * k).toPrecision(4)) };
    const id = await this.createObject(CoreTypes.text, { x: w.x, y: w.y - size.h / 2, w: size.w, h: size.h });
    if (id) this.startTextEdit(id);
  }

  private onKeyDown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement | null;
    if (target && target !== this.el && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
    if (this.offerKey(e, 'down')) return;
    this.handleKey(e);
  }

  /** API 4.6: the key goes to the module of the one selected object, if it takes keys. */
  private offerKey(e: KeyboardEvent, type: 'down' | 'up'): boolean {
    // OFF with VIEW_HISTORY_ON: Alt+Left/Right walks the view history, so a module must not take those keys.
    if (VIEW_HISTORY_ON && e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) return false;
    if (e.key === 'Escape' || e.ctrlKey || e.metaKey || this.sel.size !== 1 || !this.host.captureKey) return false;
    if (!this.host.captureKey([...this.sel][0], e, type)) return false;
    e.preventDefault();
    return true;
  }

  /** Keyboard shortcuts; returns true if handled. Exposed so the app can route global keys. */
  handleKey(e: KeyboardEvent): boolean {
    const mod = e.ctrlKey || e.metaKey;
    const k = shortcutKey(e);
    const handled = (): true => {
      e.preventDefault();
      return true;
    };
    if (e.code === 'Space' && !mod) {
      if (!this.spaceDown) {
        this.spaceDown = true;
        this.updateCursor();
      }
      return handled();
    }
    if (this.pathDraft && (k === 'escape' || k === 'enter')) {
      this.finishPath(false);
      return handled();
    }
    if (this.pathDraft && (k === 'backspace' || k === 'delete')) {
      this.pathUndoNode();
      return handled();
    }
    if (this.pathEditId) {
      if (k === 'escape' || k === 'enter') {
        this.exitPathEdit();
        return handled();
      }
      if (k === 'backspace' || k === 'delete') {
        this.deletePathNode();
        return handled();
      }
    }
    if (k === 'enter' && !mod && this.sel.size === 1) {
      const only = this.doc.get([...this.sel][0]);
      if (only?.type === CoreTypes.path && !only.locked && !this.readOnly) {
        this.enterPathEdit(only.id);
        return handled();
      }
    }
    if (k === 'escape') {
      if (this.drag) this.cancelDrag();
      else if (this.tool.kind !== 'select') this.setTool('select');
      else this.clearSelection();
      return handled();
    }
    if (mod && k === 'z') {
      if (e.shiftKey) this.redo();
      else this.undo();
      return handled();
    }
    if (mod && k === 'y') {
      this.redo();
      return handled();
    }
    if (mod && k === 'a') {
      this.selectAll();
      return handled();
    }
    if (k === 'tab' && !mod && !e.altKey) {
      this.selectNeighbor(e.shiftKey ? -1 : 1);
      return handled();
    }
    if (mod && (k === '=' || k === '+')) {
      this.zoomBy(1.25);
      return handled();
    }
    if (mod && k === '-') {
      this.zoomBy(0.8);
      return handled();
    }
    if (mod && k === '0') {
      this.zoomTo(1);
      return handled();
    }
    if (VIEW_HISTORY_ON && e.altKey && !mod && !e.shiftKey && (k === 'arrowleft' || k === 'arrowright')) {
      if (k === 'arrowleft') this.backView();
      else this.forwardView();
      e.stopPropagation();
      return handled();
    }
    if (e.shiftKey && (e.code === 'Digit1' || k === '!')) {
      this.fitAll();
      return handled();
    }
    if (e.shiftKey && (e.code === 'Digit2' || k === '@')) {
      this.fitSelection();
      return handled();
    }
    if (this.readOnly) return false;
    if (k === 'delete' || k === 'backspace') {
      this.deleteSelection();
      return handled();
    }
    if (mod && k === 'd') {
      this.duplicateSelection();
      return handled();
    }
    if (mod && e.altKey && !e.shiftKey && ['u', 's', 'i', 'e'].includes(k)) {
      const op = ({ u: 'union', s: 'subtract', i: 'intersect', e: 'exclude' } as const)[k as 'u' | 's' | 'i' | 'e'];
      if (op && this.canBoolean()) this.booleanOp(op);
      return handled();
    }
    if (mod && k === 'g') {
      if (e.shiftKey) this.ungroupSelection();
      else this.groupSelection();
      return handled();
    }
    if (mod && k === 'l') {
      this.toggleLock();
      return handled();
    }
    if (mod && (e.code === 'BracketRight' || e.code === 'BracketLeft')) {
      const up = e.code === 'BracketRight';
      this.reorderSelection(e.shiftKey ? (up ? 'front' : 'back') : up ? 'forward' : 'backward');
      return handled();
    }
    if (mod && e.altKey && !e.shiftKey && k.startsWith('arrow') && this.quickSource()) {
      void this.quickAdd(k.slice(5) as QuickDir);
      return handled();
    }
    if (k.startsWith('arrow') && this.sel.size) {
      const d = e.shiftKey ? 10 : 1;
      this.nudge(k === 'arrowleft' ? -d : k === 'arrowright' ? d : 0, k === 'arrowup' ? -d : k === 'arrowdown' ? d : 0);
      return handled();
    }
    if (k === 'enter' && this.sel.size === 1) {
      this.startTextEdit(this.selection[0]);
      return handled();
    }
    if (!mod && !e.altKey) {
      const map: Record<string, string> = { v: 'select', h: 'hand', q: 'lasso', r: 'rect', o: 'ellipse', t: 'text', s: 'sticker', f: 'frame', l: 'line', c: 'connector', p: 'path', b: 'pen', e: 'eraser' };
      const tool = map[k];
      if (tool) {
        this.setTool(tool);
        return handled();
      }
    }
    return false;
  }

  /**
   * Moving the view (middle button, hand, space, right-drag, or a drag on empty space).
   * While text is being typed this must not focus the canvas: focus would blur the editor and end it.
   */
  private navigatesCanvas(e: PointerEvent, w: Vec2): boolean {
    if (e.button === 1 || this.spaceDown || this.tool.kind === 'hand') return true;
    if (e.button === 2 && this.settings.rightDragPan) return true;
    if (e.button === 0 && !e.shiftKey && this.settings.emptyDrag === 'pan') return !this.scene.hitTest(w, 4 / this.renderer.camera.zoom);
    return false;
  }

  private startPan(e: PointerEvent, p: Vec2, clickAway = false): void {
    try {
      (e.target as Element).setPointerCapture?.(e.pointerId);
    } catch {
      // synthetic or already released pointers cannot be captured
    }
    if (e.button !== 2) e.preventDefault();
    if (e.button === 2) this.rightSeqAt = performance.now();
    this.drag = {
      kind: 'pan',
      lastX: p.x,
      lastY: p.y,
      moved: 0,
      ...(clickAway ? { clickAway: true } : {}),
      ...(e.button === 2 ? { right: { moved: 0, x: e.clientX, y: e.clientY } } : {}),
    };
    this.updateCursor();
  }

  /** Ctrl / ⌘ held over the canvas turns the move cursor into a copy cursor. */
  private noteCopyKey(e: { ctrlKey: boolean; metaKey: boolean }): void {
    const next = dragCopies(e);
    if (next === this.copyArmed) return;
    this.copyArmed = next;
    if (this.drag) return;
    const p = this.lastPointer;
    this.updateHover(p, this.renderer.camera.screenToWorld(p.x, p.y));
  }

  private onPointerDown(e: PointerEvent): void {
    this.noteCopyKey(e);
    // a floating window is still in the DOM (it closes on this same press): do not select or deselect
    if (this.host.preserveSelection?.(e)) {
      e.preventDefault();
      return;
    }
    const p = this.local(e);
    this.lastPointer = p;
    const cam = this.renderer.camera;
    const w = cam.screenToWorld(p.x, p.y);
    // panning or zooming the canvas keeps text editing; only a real click away from it ends the mode
    if (this.editingId && this.navigatesCanvas(e, w)) {
      this.startPan(e, p, e.button === 0);
      return;
    }
    this.el?.focus({ preventScroll: true });
    if (this.editingId) return; // text editor overlay handles its own blur
    if (e.button === 2) {
      if (!this.settings.rightDragPan) return;
      this.startPan(e, p);
      return;
    }
    try {
      (e.target as Element).setPointerCapture?.(e.pointerId);
    } catch {
      // synthetic or already released pointers cannot be captured
    }
    this.pasteCount = 0;

    if (e.button === 1 || this.spaceDown || this.tool.kind === 'hand') {
      this.startPan(e, p);
      return;
    }
    if (e.button !== 0) return;

    if (this.pathEditId && this.tool.kind === 'select' && this.pathEditDown(w, e.altKey)) return;

    if (this.tool.kind === 'box' || this.tool.kind === 'text') {
      const s = this.snapPoint(w);
      this.drag = { kind: 'create', tool: this.tool, start: s, cur: s };
      return;
    }
    if (this.tool.kind === 'path') {
      this.pathDown(w);
      return;
    }
    if (this.tool.kind === 'pen') {
      const mul = stylusMul(e);
      this.drag = { kind: 'pen', pts: [w.x, w.y], ...(mul !== undefined ? { pressure: [mul] } : {}) };
      return;
    }
    if (this.tool.kind === 'gesture') {
      if (this.readOnly) return;
      const s = this.tool.gesture === 'path' ? w : this.snapPoint(w);
      this.drag = { kind: 'gesture', tool: this.tool, pts: [s.x, s.y] };
      return;
    }
    if (this.tool.kind === 'lasso') {
      // Shift adds to the selection, as with the frame
      const base = e.shiftKey ? new Set(this.sel) : new Set<Id>();
      if (!e.shiftKey) this.clearSelection();
      this.drag = { kind: 'lasso', pts: [w.x, w.y], base };
      return;
    }
    if (this.tool.kind === 'eraser') {
      if (this.readOnly) return;
      // objects under the eraser only fade while dragging; they go away on release
      this.drag = { kind: 'erase', last: w, erased: new Set() };
      this.eraseAlong(this.drag, w);
      return;
    }
    if (this.tool.kind === 'line' || this.tool.kind === 'connector') {
      const startBind = this.bindAt(w) ?? undefined;
      const s = startBind ? startBind.point : this.snapPoint(w);
      this.drag = { kind: 'line', tool: this.tool, start: s, cur: s, startBind };
      this.bindHover = startBind?.objectId ?? null;
      return;
    }

    // select tool
    const knob = this.readOnly || e.shiftKey ? null : this.hitKnob(p);
    if (knob) {
      this.setCollapsed(knob.ids, !knob.collapsed);
      return;
    }
    const handles = this.selectionHandles();
    const h = hitHandle(handles, p.x, p.y);
    if (h) {
      this.beginTransform(h, w);
      return;
    }
    const q = this.hitQuick(p);
    if (q) {
      this.drag = { kind: 'quick', dir: q, src: this.selection[0], start: p, cur: w, moved: false };
      return;
    }
    const hit = this.scene.hitTest(w, 4 / cam.zoom);
    if (hit) {
      const root = cmd.topAncestor(this.doc, hit);
      // a clickable area of a module object (a button, a checkbox): a click acts, a drag still moves
      const ho = this.doc.get(hit);
      if (ho && !e.shiftKey && !this.readOnly && !ho.locked && this.host.typeActions?.(ho.type)) {
        const act = this.scene.actionAt(ho, w);
        if (act) {
          this.drag = { kind: 'pending-move', startScreen: p, startWorld: w, hit: root, action: { objectId: hit, ...act } };
          return;
        }
      }
      if (e.shiftKey) {
        this.select([root], 'toggle');
        return;
      }
      const ro = this.doc.get(root)!;
      // the border moves the frame straight away; the inside still selects its contents
      if (ro.type === CoreTypes.frame && !this.sel.has(root) && worldToLocal(ro, w).y >= 0 && !this.frameBorder(ro, w)) {
        this.clearSelection();
        this.drag = { kind: 'marquee', start: w, cur: w, additive: false, base: new Set(), frameClick: root };
        return;
      }
      if (!this.sel.has(root)) this.select([root]);
      this.drag = { kind: 'pending-move', startScreen: p, startWorld: w, hit: root };
      return;
    }
    // the selection box is draggable even where the pointer misses the outline
    if (this.inSelectionBox(w)) {
      this.drag = { kind: 'pending-move', startScreen: p, startWorld: w, hit: this.selection[0] };
      return;
    }
    if (this.settings.emptyDrag === 'pan' && !e.shiftKey) {
      this.clearSelection();
      this.drag = { kind: 'pan', lastX: p.x, lastY: p.y, moved: 0 };
      this.updateCursor();
      return;
    }
    const base = e.shiftKey ? new Set(this.sel) : new Set<Id>();
    if (!e.shiftKey) this.clearSelection();
    this.drag = { kind: 'marquee', start: w, cur: w, additive: e.shiftKey, base };
  }

  private onPointerMove(e: PointerEvent): void {
    this.noteCopyKey(e);
    const p = this.local(e);
    this.lastPointer = p;
    const cam = this.renderer.camera;
    const w = cam.screenToWorld(p.x, p.y);
    const d = this.drag;
    if (this.pathDraft && (!d || d.kind !== 'pan')) {
      this.pathMove(w, (e.buttons & 1) === 1, e.altKey);
      return;
    }
    if (!d) {
      if (this.tool.kind === 'eraser') this.renderer.invalidate();
      this.updateHover(p, w);
      return;
    }
    switch (d.kind) {
      case 'pan': {
        const step = Math.hypot(p.x - d.lastX, p.y - d.lastY);
        d.moved += step;
        if (d.right) d.right.moved += step;
        cam.panBy(p.x - d.lastX, p.y - d.lastY);
        d.lastX = p.x;
        d.lastY = p.y;
        return;
      }
      case 'marquee': {
        d.cur = w;
        const r = normalizeRect(d.start.x, d.start.y, w.x, w.y);
        const contain = (this.settings.marqueeMode === 'contain') !== e.altKey;
        const found = new Set(d.base);
        for (const id of this.scene.query(r)) {
          const o = this.doc.get(id);
          if (!o) continue;
          const b = boxBounds(o);
          const fullOnly = contain || o.type === CoreTypes.frame;
          if (fullOnly ? rectContainsRect(r, b) : rectsIntersect(r, b)) found.add(cmd.topAncestor(this.doc, id));
        }
        // groups: in contain mode require the whole group inside
        if (contain) {
          for (const id of [...found]) {
            if (d.base.has(id)) continue;
            const o = this.doc.get(id);
            if (o && o.type === CoreTypes.group && !rectContainsRect(r, boxBounds(o))) found.delete(id);
          }
        }
        this.sel = found;
        this.emit('selection', this.selection);
        this.renderer.invalidate();
        return;
      }
      case 'pending-move': {
        if (Math.hypot(p.x - d.startScreen.x, p.y - d.startScreen.y) < CLICK_SLOP) return;
        if (this.readOnly) return;
        // dragging from a clickable area moves the object like any drag
        if (d.action && !this.sel.has(d.hit)) this.select([d.hit]);
        let roots = this.selection.filter((id) => {
          const o = this.doc.get(id);
          return o && this.caps(o).move;
        });
        if (!roots.length || !this.checkConflict(cmd.withContents(this.doc, roots), 'move')) {
          this.drag = null;
          return;
        }
        // Ctrl/⌘ at the moment the drag starts: the originals stay, the copies follow the pointer
        const copying = dragCopies(e);
        const copyFrom = copying ? [...this.selection] : undefined;
        let tx: TxContext | null = null;
        try {
          tx = this.doc.begin({ label: copying ? 'duplicate' : 'move', source: { kind: 'user' } });
          if (copying) {
            const by = this.host.objectAuthor?.();
            if (by) {
              const origCreate = tx.create.bind(tx);
              tx.create = (rec) => origCreate({ ...rec, addedBy: by });
            }
            const copies = cmd.duplicate(tx, roots, 0);
            if (!copies.length) {
              tx.rollback();
              this.drag = null;
              return;
            }
            this.select(copies);
            roots = copies;
          }
          const all = cmd.withContents(this.doc, [...roots, ...this.scene.foldedUnder(cmd.withContents(this.doc, roots))]);
          const orig = new Map<Id, { x: number; y: number }>();
          for (const id of all) {
            const o = this.doc.get(id)!;
            orig.set(id, { x: o.x, y: o.y });
          }
          const anchor = cmd.selectionBounds(this.doc, roots)!;
          this.drag = { kind: 'move', tx, startWorld: d.startWorld, orig, anchor, roots, targets: this.guideTargets(new Set(all)), guides: [], ...(copyFrom ? { copyFrom } : {}) };
        } catch (err) {
          if (tx?.isOpen) tx.rollback();
          this.drag = null;
          this.reportError(err);
          return;
        }
        this.onPointerMove(e);
        return;
      }
      case 'move': {
        let dx = w.x - d.startWorld.x;
        let dy = w.y - d.startWorld.y;
        if (e.shiftKey) {
          if (Math.abs(dx) > Math.abs(dy)) dy = 0;
          else dx = 0;
        }
        d.guides = [];
        if (this.settings.snapToGrid) {
          const st = this.settings.grid.step;
          dx = snap(d.anchor.x + dx, st) - d.anchor.x;
          dy = snap(d.anchor.y + dy, st) - d.anchor.y;
        } else if (this.settings.smartGuides && !e.altKey) {
          const g = this.snapToGuides(d.anchor, dx, dy, d.targets, e.shiftKey);
          dx = g.dx;
          dy = g.dy;
          d.guides = g.guides;
          d.gaps = g.gaps;
        }
        try {
          for (const [id, o] of d.orig) d.tx.update(id, { x: o.x + dx, y: o.y + dy });
          const parents = new Set<Id>();
          for (const id of d.roots) {
            const pid = this.doc.get(id)?.parentId;
            if (pid) parents.add(pid);
          }
          for (const pid of parents) cmd.updateGroupBounds(d.tx, pid);
          this.rerouteFor(d.tx, new Set(d.orig.keys()));
          // highlight the frame the selection will land in
          d.dropFrame = this.frameUnder({ x: d.anchor.x + dx + d.anchor.w / 2, y: d.anchor.y + dy + d.anchor.h / 2 }, new Set(d.orig.keys()));
        } catch (err) {
          this.cancelDrag();
          this.reportError(err);
        }
        return;
      }
      case 'resize':
        this.applyResize(d, w, e.shiftKey || this.ratioLocked(), this.settings.smartGuides && !e.altKey && !this.settings.snapToGrid);
        return;
      case 'rotate':
        this.applyRotate(d, w, e.shiftKey);
        return;
      case 'create':
        d.cur = this.snapPoint(w);
        this.renderer.invalidate();
        return;
      case 'line': {
        const bind = this.bindAt(w, d.startBind?.objectId && !d.startBind.t && !d.startBind.anchor ? new Set([d.startBind.objectId]) : undefined);
        d.endBind = bind ?? undefined;
        d.cur = bind ? bind.point : this.snapPoint(w);
        this.bindHover = bind?.objectId ?? null;
        this.renderer.invalidate();
        return;
      }
      case 'endpoint':
        this.applyEndpoint(d, w);
        return;
      case 'quick': {
        if (!d.moved && Math.hypot(p.x - d.start.x, p.y - d.start.y) < CLICK_SLOP * 2) return;
        d.moved = true;
        d.cur = w;
        const bind = this.bindAt(w, new Set([d.src]));
        const target = bind?.objectId ? this.doc.get(bind.objectId) : undefined;
        d.bind = bind && target && !isLineLike(target) ? bind : undefined;
        this.bindHover = d.bind?.objectId ?? null;
        this.renderer.invalidate();
        return;
      }
      case 'node': {
        const base = d.base;
        const l = worldToLocal(base, w);
        if (!d.moved && Math.hypot(l.x - d.start.x, l.y - d.start.y) * cam.zoom < 2) return;
        d.moved = true;
        this.applyPathNodes(d.tx, base, dragPart(d.orig, d.index, d.part, l, d.start, e.altKey));
        this.renderer.invalidate();
        return;
      }
      case 'pen': {
        const n = d.pts.length;
        if (Math.hypot(w.x - d.pts[n - 2], w.y - d.pts[n - 1]) * cam.zoom >= 1.5) {
          d.pts.push(w.x, w.y);
          const mul = stylusMul(e);
          if (d.pressure && mul !== undefined) d.pressure.push(mul);
        }
        this.renderer.invalidate();
        return;
      }
      case 'gesture': {
        if (d.tool.gesture === 'line') {
          const s = this.snapPoint(w);
          d.pts = [d.pts[0], d.pts[1], s.x, s.y];
        } else if (d.tool.gesture === 'path') {
          const n = d.pts.length;
          if (Math.hypot(w.x - d.pts[n - 2], w.y - d.pts[n - 1]) * cam.zoom >= 1.5) d.pts.push(w.x, w.y);
        }
        this.renderer.invalidate();
        return;
      }
      case 'erase':
        this.eraseAlong(d, w);
        return;
      case 'lasso': {
        const n = d.pts.length;
        // a point every couple of screen pixels is plenty
        if (Math.hypot(w.x - d.pts[n - 2], w.y - d.pts[n - 1]) * this.renderer.camera.zoom < 3) return;
        d.pts.push(w.x, w.y);
        this.lassoSelect(d, (this.settings.marqueeMode === 'contain') !== e.altKey);
        this.renderer.invalidate();
        return;
      }
    }
  }

  /** Objects inside the lasso (whole or touched, as the frame selection is set up), by their top group. */
  private lassoSelect(d: Extract<Drag, { kind: 'lasso' }>, contain: boolean): void {
    const found = new Set(d.base);
    if (d.pts.length >= 6) {
      const bounds = pointsBounds(d.pts);
      for (const id of this.scene.query(bounds)) {
        const o = this.doc.get(id);
        if (!o) continue;
        const root = cmd.topAncestor(this.doc, id);
        const b = boxBounds(root === id ? o : this.doc.get(root)!);
        if (contain || o.type === CoreTypes.frame ? boxInPolygon(b, d.pts) : boxTouchesPolygon(b, d.pts)) found.add(root);
      }
    }
    this.sel = found;
    this.emit('selection', this.selection);
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.pathDraft) this.pathDraft.dragging = -1;
    const d = this.drag;
    this.drag = null;
    if (!d) return;
    const p = this.local(e);
    const w = this.renderer.camera.screenToWorld(p.x, p.y);
    this.lastPointer = p;
    try {
      switch (d.kind) {
      case 'pan':
        this.updateCursor();
        // a click on empty space still finishes typing; dragging the canvas does not
        if (d.clickAway && d.moved < 5) this.el?.focus({ preventScroll: true });
        if (d.right) {
          this.rightSeqAt = performance.now();
          if (d.right.moved < 5) this.openMenuAt(d.right.x, d.right.y);
        }
        return;
      case 'marquee':
        // a click (no drag) on a frame's body selects the frame
        if (d.frameClick && Math.hypot(d.cur.x - d.start.x, d.cur.y - d.start.y) * this.renderer.camera.zoom < CLICK_SLOP) this.select([d.frameClick]);
        this.renderer.invalidate();
        return;
      case 'pending-move':
        if (d.action) {
          this.host.onAction?.(d.action.objectId, { id: d.action.id, ...(d.action.data !== undefined ? { data: d.action.data } : {}) });
          return;
        }
        // plain click on an already-selected object inside multi-selection selects it alone
        if (!e.shiftKey && this.sel.size > 1 && this.sel.has(d.hit)) {
          const hit = this.scene.hitTest(w, 4 / this.renderer.camera.zoom);
          if (hit) this.select([hit]);
        }
        return;
      case 'move':
      case 'resize':
      case 'rotate':
        try {
          if (d.kind === 'move') cmd.assignFrames(d.tx, d.roots);
          else if (d.kind === 'resize') {
            for (const id of this.selection) if (this.doc.get(id)?.type === CoreTypes.frame) cmd.adoptIntoFrame(d.tx, id);
            cmd.assignFrames(d.tx, this.selection);
          }
          d.tx.commit();
        } catch (err) {
          this.reportError(err);
        }
        this.renderer.invalidate();
        return;
      case 'create':
        void this.finishCreate(d);
        return;
      case 'line':
        this.bindHover = null;
        void this.finishLine(d, w);
        return;
      case 'endpoint':
        this.bindHover = null;
        this.finishEndpoint(d);
        return;
      case 'quick':
        this.bindHover = null;
        if (!d.moved) void this.quickAdd(d.dir);
        else if (d.bind) this.quickConnect(d.src, d.dir, d.bind);
        else void this.quickAdd(d.dir, d.cur);
        this.renderer.invalidate();
        return;
      case 'pen':
        this.finishPen(d);
        return;
      case 'gesture': {
        const kind = d.tool.gesture ?? 'click';
        const pts: Vec2[] = [];
        for (let i = 0; i < d.pts.length; i += 2) pts.push({ x: d.pts[i], y: d.pts[i + 1] });
        // a click with a line tool is not a line
        if (kind === 'line' && pts.length < 2) return;
        this.setTool('select');
        this.host.onToolGesture?.(d.tool, { kind, points: kind === 'click' ? pts.slice(0, 1) : kind === 'path' ? simplifyPolyline(d.pts, 0.6 / this.renderer.camera.zoom).reduce<Vec2[]>((a, v, i, arr) => (i % 2 === 0 ? [...a, { x: v, y: arr[i + 1] }] : a), []) : pts });
        this.renderer.invalidate();
        return;
      }
      case 'node':
        try {
          if (!d.moved && d.part === 'convert') this.applyPathNodes(d.tx, d.base, makeCorner(d.orig, d.index));
          if (d.moved || d.part === 'convert') d.tx.commit();
          else d.tx.rollback();
        } catch (err) {
          this.reportError(err);
        }
        this.renderer.invalidate();
        return;
      case 'lasso':
        // the selection was made while drawing; the tool stays for the next outline
        this.renderer.invalidate();
        return;
      case 'erase': {
        const ids = [...d.erased].filter((id) => this.doc.has(id));
        if (ids.length) this.edit('erase', (tx) => cmd.deleteDeep(tx, ids));
        this.renderer.invalidate();
        return;
      }
    }
    } finally {
      // the rings follow whatever is under the pointer now (a pan or a marquee leaves hover stale)
      this.updateHover(p, w);
    }
  }

  private cancelDrag(): void {
    const d = this.drag;
    this.drag = null;
    const copyFrom = d?.kind === 'move' ? d.copyFrom : undefined;
    if (d && (d.kind === 'move' || d.kind === 'resize' || d.kind === 'rotate' || d.kind === 'endpoint' || d.kind === 'node') && d.tx.isOpen) d.tx.rollback();
    if (copyFrom) this.select(copyFrom);
    this.bindHover = null;
    this.knobLatch = null;
    this.knobHover = null;
    this.updateCursor();
    this.renderer.invalidate();
  }

  private updateHover(p: Vec2, w: Vec2): void {
    if (this.pathEditId && this.tool.kind === 'select') {
      const h = this.pathEditHit(w);
      this.pathHover = h?.kind === 'segment' ? h.point : null;
      this.setCursor(h ? (h.kind === 'segment' ? 'copy' : 'move') : 'default');
      this.renderer.invalidate();
      return;
    }
    const lineTool = this.tool.kind === 'connector' || this.tool.kind === 'line';
    const hit = this.tool.kind === 'select' ? this.scene.hitTest(w, 4 / this.renderer.camera.zoom) : lineTool ? this.bindAt(w)?.objectId ?? null : null;
    const root = hit ? (lineTool ? hit : cmd.topAncestor(this.doc, hit)) : null;
    if (root !== this.hoverId) {
      this.setHover(root);
      this.renderer.invalidate();
    }
    // latch first: the ring has to be hittable on the same move that reaches it
    this.syncKnobLatch(p);
    let cursor: string | null = null;
    if (this.tool.kind === 'select' && !this.spaceDown) {
      const knob = this.readOnly ? null : this.hitKnob(p);
      if ((knob?.id ?? null) !== this.knobHover) {
        this.knobHover = knob?.id ?? null;
        this.renderer.invalidate();
      }
      const h = knob ? null : hitHandle(this.selectionHandles(), p.x, p.y);
      // a "connected shape" point under the pointer turns into an arrow and previews the shape it adds
      const q = h || knob ? null : this.hitQuick(p);
      if (knob) cursor = 'pointer';
      else if (h) cursor = cursorFor(h, this.selectionBox()?.rotation ?? 0);
      else if (q) cursor = 'pointer';
      if (q !== this.quickHoverShown) {
        this.quickHoverShown = q;
        this.renderer.invalidate();
      }
    }
    // a clickable area of a module object shows it can be pressed
    if (!cursor && hit && this.tool.kind === 'select' && !this.spaceDown && !this.readOnly) {
      const ho = this.doc.get(hit);
      if (ho && !ho.locked && this.host.typeActions?.(ho.type) && this.scene.actionAt(ho, w)) cursor = 'pointer';
    }
    if (cursor) this.setCursor(cursor);
    else if (this.tool.kind === 'select' && !this.spaceDown) {
      const o = root ? this.doc.get(root) : null;
      // the inside of an unselected frame selects contents, so the cursor stays the plain one
      const frameBody = !!o && o.type === CoreTypes.frame && !this.sel.has(o.id) && worldToLocal(o, w).y >= 0 && !this.frameBorder(o, w);
      const inBox = !frameBody && this.inSelectionBox(w);
      const canDrag = (!!root && !frameBody) || inBox;
      const copy = canDrag && this.copyArmed && !this.readOnly && !(o?.locked && !inBox);
      this.setCursor(canDrag ? (copy ? 'copy' : 'move') : 'default');
    } else this.updateCursor();
  }

  /** Pointer is inside the selection box (the frame drawn around the selection). */
  private inSelectionBox(w: Vec2): boolean {
    if (this.sel.size === 0) return false;
    const box = this.selectionBox();
    if (!box) return false;
    const l = worldToLocal(box, w);
    return l.x >= 0 && l.y >= 0 && l.x <= box.w && l.y <= box.h;
  }

  /** Pointer is on a frame's stroke (screen slop plus half the stroke), not on its title or its inside. */
  private frameBorder(o: ObjectRecord, w: Vec2): boolean {
    if (o.type !== CoreTypes.frame) return false;
    const l = worldToLocal(o, w);
    const tol = (8 + (o.style.stroke?.width ?? 0) / 2) / this.renderer.camera.zoom;
    if (l.x < -tol || l.y < -tol || l.x > o.w + tol || l.y > o.h + tol) return false;
    return Math.abs(l.x) <= tol || Math.abs(l.x - o.w) <= tol || Math.abs(l.y) <= tol || Math.abs(l.y - o.h) <= tol;
  }

  private snapPoint(w: Vec2): Vec2 {
    if (!this.settings.snapToGrid) return w;
    const st = this.settings.grid.step;
    return { x: snap(w.x, st), y: snap(w.y, st) };
  }

  /** Final rectangle of a shape being dragged out (shared by the live preview and creation). */
  private createRect(d: Extract<Drag, { kind: 'create' }>): { rect: Rect; tiny: boolean } {
    const tool = d.tool;
    const k = this.viewScale;
    const base = tool.defaultSize ?? { w: 100, h: 100 };
    const size = { w: parseFloat((base.w * k).toPrecision(4)), h: parseFloat((base.h * k).toPrecision(4)) };
    let r = normalizeRect(d.start.x, d.start.y, d.cur.x, d.cur.y);
    const tiny = r.w * this.renderer.camera.zoom < 4 && r.h * this.renderer.camera.zoom < 4;
    if (tiny) r = { x: d.start.x - size.w / 2, y: d.start.y - size.h / 2, w: size.w, h: size.h };
    if (tool.objectType === CoreTypes.sticker && !tiny) {
      const s = Math.max(r.w, r.h);
      r = { x: r.x, y: r.y, w: s, h: s };
    }
    if (tool.kind === 'text' && tiny) r = { x: d.start.x, y: d.start.y - size.h / 2, w: size.w, h: size.h };
    r.w = Math.max(r.w, 2 * k);
    r.h = Math.max(r.h, 2 * k);
    return { rect: r, tiny };
  }

  private async finishCreate(d: Extract<Drag, { kind: 'create' }>): Promise<void> {
    const tool = d.tool;
    const r = this.createRect(d).rect;
    this.setTool('select'); // §6.2: return to the select tool after creation
    const id = await this.createObject(tool.objectType!, r, { style: this.toolStyle(tool), props: tool.props && { ...tool.props } });
    if (id && (tool.objectType === CoreTypes.text || tool.objectType === CoreTypes.sticker || tool.style || tool.props)) this.startTextEdit(id);
  }

  /**
   * The object the current drawing gesture will produce, for the live preview: the overlay
   * renders it with the regular display pipeline, so it looks exactly like the final result.
   */
  get ghost(): ObjectRecord | null {
    if (this.pathDraft) return this.pathGhost();
    const d = this.drag;
    if (!d) return null;
    const cid = this.doc.canvasId;
    if (d.kind === 'pen') return d.pts.length >= 2 ? this.penRecord(d.pts, 'a0', d.pressure) : null;
    // a module tool's line or stroke: a thin preview of the gesture
    if (d.kind === 'gesture') return d.tool.gesture !== 'click' && d.pts.length >= 4 ? this.penRecord(d.pts, 'a0', undefined, true) : null;
    if (d.kind === 'create') {
      const type = d.tool.objectType;
      if (!type || !type.startsWith('core:') || d.tool.kind === 'text') return null;
      const { rect, tiny } = this.createRect(d);
      if (tiny) return null;
      return makeObject(cid, { type, ...rect, z: 'a0', props: d.tool.props && { ...d.tool.props }, style: { ...this.scaledStyle(type), ...this.toolStyle(d.tool) } });
    }
    if (d.kind === 'line') {
      if (Math.hypot(d.cur.x - d.start.x, d.cur.y - d.start.y) * this.renderer.camera.zoom < 4) return null;
      const arrowEnd = d.tool.id !== 'line';
      if (d.tool.kind === 'line' && !d.startBind && !d.endBind) {
        const { rect, points } = this.freeLine(d, d.cur);
        return makeObject(cid, { type: CoreTypes.line, ...rect, z: 'a0', style: this.scaledStyle(CoreTypes.line), props: { points, arrowEnd, arrowStart: false } });
      }
      const start: ConnectorEnd = d.startBind ?? { point: d.start };
      const end: ConnectorEnd = d.endBind ?? { point: d.cur };
      const style = this.scaledStyle(CoreTypes.connector);
      const unit = Math.max(0.05, (style?.stroke?.width ?? 2) / 2);
      const r = computeRoute(this.doc, start, end, 'orthogonal', (q) => this.scene.query(q), undefined, unit);
      const box = pointsBox(r.pts);
      return makeObject(cid, {
        type: CoreTypes.connector,
        x: box.x,
        y: box.y,
        w: box.w,
        h: box.h,
        z: 'a0',
        style,
        props: { start: { ...start, point: r.a }, end: { ...end, point: r.b }, route: 'orthogonal', arrowEnd, arrowStart: false, path: box.local },
      });
    }
    return null;
  }

  /** Plain line geometry for a free line/arrow drag (shared by the live preview and creation). */
  private freeLine(d: Extract<Drag, { kind: 'line' }>, end: Vec2): { rect: Rect; points: number[] } {
    const x0 = Math.min(d.start.x, end.x);
    const y0 = Math.min(d.start.y, end.y);
    return { rect: { x: x0, y: y0, w: Math.abs(end.x - d.start.x), h: Math.abs(end.y - d.start.y) }, points: [d.start.x - x0, d.start.y - y0, end.x - x0, end.y - y0] };
  }

  private async finishLine(d: Extract<Drag, { kind: 'line' }>, w: Vec2): Promise<void> {
    const tool = d.tool;
    const cam = this.renderer.camera;
    let end = d.cur;
    if (Math.hypot(end.x - d.start.x, end.y - d.start.y) * cam.zoom < 4) {
      end = { x: d.start.x + 160 / cam.zoom, y: d.start.y };
    }
    this.setTool('select');
    void w;
    const arrowEnd = tool.id !== 'line';
    // a free line (no end glued to anything) stays a plain line
    if (tool.kind === 'line' && !d.startBind && !d.endBind) {
      const { rect, points } = this.freeLine(d, end);
      await this.createObject(CoreTypes.line, rect, { props: { points, arrowEnd, arrowStart: false } });
      return;
    }
    // glued to objects/connectors: a connector that routes around obstacles (§5.4)
    const start: ConnectorEnd = d.startBind ?? { point: d.start };
    const endBind: ConnectorEnd = d.endBind ?? { point: end };
    const id = this.createConnector(start, endBind, { arrowEnd });
    if (id) this.select([id]);
  }

  /** Topmost frame under a world point, ignoring the excluded ids. */
  frameUnder(p: Vec2, exclude: Set<Id>): Id | null {
    for (const id of this.scene.hitTestAll(p, 0)) {
      const o = this.doc.get(id);
      if (o && o.type === CoreTypes.frame && !exclude.has(id)) return id;
    }
    return null;
  }

  /** Remove the selected frames but keep their contents. */
  unframeSelection(): void {
    const frames = this.selection.filter((id) => this.doc.get(id)?.type === CoreTypes.frame);
    if (!frames.length) return;
    const freed = this.edit('unframe', (tx) => frames.flatMap((f) => cmd.unframe(tx, f)));
    if (freed) this.select(freed);
  }

  /** Create a routed connector between two ends. */
  private createConnector(start: ConnectorEnd, end: ConnectorEnd, extra: { arrowEnd?: boolean; arrowStart?: boolean; style?: Partial<Style>; label?: string } = {}): Id | undefined {
    return this.edit('create', (tx) => this.connectorIn(tx, start, end, extra));
  }

  /** An arrow glued to its ends, routed, inside a running transaction. */
  private connectorIn(tx: TxContext, start: ConnectorEnd, end: ConnectorEnd, extra: { arrowEnd?: boolean; arrowStart?: boolean; style?: Partial<Style>; label?: string; route?: string } = {}): Id {
    {
      const o = makeObject(this.doc.canvasId, {
        type: CoreTypes.connector,
        x: Math.min(start.point.x, end.point.x),
        y: Math.min(start.point.y, end.point.y),
        w: Math.abs(end.point.x - start.point.x),
        h: Math.abs(end.point.y - start.point.y),
        z: cmd.nextZ(this.doc),
        style: { ...(this.scaledStyle(CoreTypes.connector) ?? {}), ...(extra.style ?? {}) },
        props: { start, end, route: extra.route ?? 'orthogonal', arrowEnd: extra.arrowEnd ?? true, arrowStart: extra.arrowStart ?? false, path: [0, 0, 1, 1], ...(extra.label ? { label: extra.label } : {}) },
      });
      tx.create(o);
      rerouteConnector(tx, this.doc.get(o.id)!, (r) => this.scene.query(r));
      if (extra.label) {
        const cur = this.doc.get(o.id)!;
        const pts = cur.props['path'] as number[] | undefined;
        if (Array.isArray(pts) && pts.length >= 4) {
          const base = cur.style.text ?? DEFAULT_TEXT_STYLE;
          tx.update(o.id, { style: { ...cur.style, text: { ...base, fontSize: fitArrowLabel(extra.label, base, pathLength(pts)) } } });
        }
      }
      return o.id;
    }
  }

  /**
   * Objects and arrows between them in one step (module trees and templates): frames take what they
   * cover, arrows glue to their ends (index of a new object or an existing id). `at` moves the whole
   * set so its top-left lands there. Ids of the new objects in order.
   */
  insertTree(
    objects: { type: string; x: number; y: number; w: number; h: number; props: Record<string, unknown>; style?: Record<string, unknown>; name?: string; formatVersion?: number }[],
    connectors: { from: number | Id; to: number | Id; label?: string; arrow?: boolean }[],
    at?: Vec2,
    module?: string,
    ownOnly = false,
  ): Id[] {
    const minX = Math.min(...objects.map((o) => o.x));
    const minY = Math.min(...objects.map((o) => o.y));
    const dx = at ? at.x - minX : 0;
    const dy = at ? at.y - minY : 0;
    return (
      this.edit('insert', (tx) => {
        const ids: Id[] = [];
        for (const o of objects) {
          const rec = makeObject(this.doc.canvasId, {
            type: o.type,
            x: o.x + dx,
            y: o.y + dy,
            w: o.w,
            h: o.h,
            z: cmd.nextZ(this.doc),
            props: o.props,
            // a module type: its text style (manifest `text.style`) and look (`defaultStyle`), as for a drawn object
            style: {
              ...(o.type.startsWith('core:') ? {} : { ...(this.host.typeText?.(o.type) ? { text: this.host.typeText(o.type)!.style } : {}), ...(this.host.typeStyle?.(o.type) ?? {}) }),
              ...((o.style as Partial<Style> | undefined) ?? {}),
            },
            formatVersion: o.formatVersion,
            ...(o.name !== undefined ? { name: o.name } : {}),
          });
          tx.create(rec);
          ids.push(rec.id);
        }
        const frames = ids.filter((id) => this.doc.get(id)?.type === CoreTypes.frame);
        // `ownOnly`: the new frames take only the new objects, never ones already on the canvas
        for (const f of frames) cmd.adoptIntoFrame(tx, f, ownOnly ? new Set(ids) : undefined);
        cmd.assignFrames(tx, ids.filter((id) => !frames.includes(id)));
        const center = (id: Id): Vec2 => {
          const o = this.doc.get(id)!;
          return { x: o.x + o.w / 2, y: o.y + o.h / 2 };
        };
        for (const c of connectors) {
          const a = typeof c.from === 'number' ? ids[c.from] : c.from;
          const b = typeof c.to === 'number' ? ids[c.to] : c.to;
          if (!this.doc.get(a) || !this.doc.get(b)) continue;
          ids.push(this.connectorIn(tx, { objectId: a, point: center(a) }, { objectId: b, point: center(b) }, { arrowEnd: c.arrow ?? true, label: c.label }));
        }
        return ids;
      }, module) ?? []
    );
  }

  // ---------- folding branches (mind-map style) ----------

  /** Branch connectors meant by `ids`: connectors themselves, or every branch leaving a shape. */
  private branchConnectors(ids: Id[]): Id[] {
    const out = new Set<Id>();
    for (const id of ids) {
      if (this.scene.branchOf(id)) out.add(id);
      else for (const b of this.scene.branchesFrom(id)) out.add(b.conn);
    }
    return [...out];
  }

  /**
   * Collapse or expand branches (one undo step): a connector folds away the shape it leads to and all
   * that hangs from it, a shape — each of its branches. What gets hidden leaves the selection.
   */
  setCollapsed(ids: Id[], on: boolean): void {
    const targets = this.branchConnectors(ids).filter((id) => (this.doc.get(id)?.collapsed === true) !== on);
    if (!targets.length || this.readOnly) return;
    this.updateObjects(
      targets.map((id) => ({ id, patch: { collapsed: on || undefined } as Partial<ObjectRecord> })),
      on ? 'collapse' : 'expand',
    );
    if (this.hoverId && this.scene.isFolded(this.hoverId)) this.setHover(null);
    const keep = this.selection.filter((id) => !this.scene.isFolded(id));
    if (keep.length !== this.sel.size) this.select(keep);
  }

  toggleCollapsed(conn: Id): void {
    this.setCollapsed([conn], this.doc.get(conn)?.collapsed !== true);
  }

  /** What folding can do with the selection (for menus and the palette). */
  foldState(): { collapse: boolean; expand: boolean } {
    const ids = this.branchConnectors(this.selection);
    return {
      collapse: ids.some((id) => this.doc.get(id)?.collapsed !== true),
      expand: ids.some((id) => this.doc.get(id)?.collapsed === true),
    };
  }

  /** Shape whose expanded collapse rings stay up. */
  private knobLatch: Id | null = null;
  /** Ring under the pointer. Kept a little past its edge so the glyph does not blink. */
  private knobHover: Id | null = null;

  private setKnobLatch(id: Id | null): void {
    if (this.knobLatch === id) return;
    this.knobLatch = id;
    this.renderer.invalidate();
  }

  /** A shape with at least one expanded branch still on the canvas, or null. */
  private expandedParent(id: Id | null): Id | null {
    if (!id) return null;
    for (const b of this.scene.branchesFrom(id)) if (!b.collapsed && !this.scene.isFolded(b.conn)) return id;
    return null;
  }

  /**
   * Which shape's rings to show. Hovering the shape shows them; leaving the shape does not hide them
   * while the pointer is still near a ring (the ring sits outside the shape, on top of the connector,
   * so the object under the cursor changes on the way there). Hovering that spot on the connector
   * shows the ring too.
   */
  private syncKnobLatch(p: Vec2): void {
    if (this.tool.kind !== 'select' || this.drag) {
      this.setKnobLatch(null);
      return;
    }
    // the ring's own area wins: whatever object is under the pointer there, the ring stays put
    if (this.knobLatch && this.nearExpandedKnobs(p, this.knobLatch)) return;
    const hovered = this.expandedParent(this.hoverId);
    if (hovered) {
      this.setKnobLatch(hovered);
      return;
    }
    const via = this.hoverId ? this.scene.branchOf(this.hoverId) : null;
    if (via && this.expandedParent(via.from) && this.nearExpandedKnobs(p, via.from)) {
      this.setKnobLatch(via.from);
      return;
    }
    this.setKnobLatch(null);
  }

  private nearExpandedKnobs(p: Vec2, parentId: Id): boolean {
    for (const k of this.expandedSpots(parentId)) if (Math.hypot(k.screen.x - p.x, k.screen.y - p.y) <= k.r + KNOB_KEEP) return true;
    return false;
  }

  private pointKey(from: Id, at: Vec2): string {
    return `${from}:${Math.round(at.x * 100)}:${Math.round(at.y * 100)}`;
  }

  /** Screen position of a knob just outside a shape, at a point on its side. */
  private knobOutward(o: ObjectRecord, at: Vec2, off: number): Vec2 {
    const box = boxBounds(o);
    const nx = (at.x - (box.x + box.w / 2)) / Math.max(box.w / 2, 1e-9);
    const ny = (at.y - (box.y + box.h / 2)) / Math.max(box.h / 2, 1e-9);
    const p = this.renderer.camera.worldToScreen(at.x, at.y);
    return Math.abs(nx) >= Math.abs(ny) ? { x: p.x + Math.sign(nx || 1) * off, y: p.y } : { x: p.x, y: p.y + Math.sign(ny || 1) * off };
  }

  /** Too short on screen: the ring would sit on the arrowhead. */
  private ringTooClose(conn: ObjectRecord, start: Vec2, end: Vec2, r: number): boolean {
    const cam = this.renderer.camera;
    const s = cam.worldToScreen(start.x, start.y);
    const e = cam.worldToScreen(end.x, end.y);
    return Math.hypot(e.x - s.x, e.y - s.y) < Math.max(64, 10 + r * 2 + arrowSize(conn.style.stroke?.width ?? 2) * cam.zoom * 2 + 16);
  }

  private collapsedPointKeys(): Set<string> {
    const keys = new Set<string>();
    for (const id of this.scene.collapsedBranches()) {
      const b = this.scene.branchOf(id);
      if (b) keys.add(this.pointKey(b.from, b.start));
    }
    return keys;
  }

  /** Expanded branches of a shape still on the canvas, by the point they leave it at. */
  private expandedByPoint(parentId: Id): Map<string, Id[]> {
    const out = new Map<string, Id[]>();
    for (const b of this.scene.branchesFrom(parentId)) {
      if (b.collapsed || this.scene.isFolded(b.conn)) continue;
      const key = this.pointKey(b.from, b.start);
      out.set(key, [...(out.get(key) ?? []), b.conn]);
    }
    return out;
  }

  /** Collapse rings of one shape's expanded branches (screen px); branches leaving the same point share one. */
  private expandedSpots(parentId: Id): { id: Id; ids: Id[]; screen: Vec2; r: number; color: string }[] {
    const from = this.doc.get(parentId);
    if (!from) return [];
    const occupied = this.collapsedPointKeys();
    const r = 6;
    const out: { id: Id; ids: Id[]; screen: Vec2; r: number; color: string }[] = [];
    for (const [key, ids] of this.expandedByPoint(parentId)) {
      if (occupied.has(key)) continue;
      const b = this.scene.branchOf(ids[0])!;
      const conn = this.doc.get(ids[0]);
      if (!conn) continue;
      if (ids.every((id) => {
        const c = this.doc.get(id);
        const cb = this.scene.branchOf(id);
        return !c || !cb || this.ringTooClose(c, cb.start, cb.end, r);
      })) continue;
      out.push({ id: ids[0], ids, screen: this.knobOutward(from, b.start, 10 + r), r, color: conn.style.stroke?.color ?? '#64748b' });
    }
    return out;
  }

  /**
   * Branch knobs. A collapsed branch disappears with its arrow; the shape it left shows a counter on a short
   * stub — how many shapes are folded away there (branches leaving the same point share one).
   * An expanded branch has a ring just outside the shape it leaves. The ring stays while the pointer is on
   * that shape and while it remains near the ring, whichever object is underneath.
   */
  foldKnobs(): { id: Id; ids: Id[]; screen: Vec2; stub?: Vec2; r: number; count: number; collapsed: boolean; hot: boolean; color: string }[] {
    const cam = this.renderer.camera;
    const out: ReturnType<Editor['foldKnobs']> = [];
    const groups = new Map<string, Id[]>();
    for (const id of this.scene.collapsedBranches()) {
      const b = this.scene.branchOf(id);
      if (!b) continue;
      const key = this.pointKey(b.from, b.start);
      groups.set(key, [...(groups.get(key) ?? []), id]);
    }
    for (const [key, ids] of groups) {
      const b = this.scene.branchOf(ids[0])!;
      const from = this.doc.get(b.from);
      const conn = this.doc.get(ids[0]);
      if (!from || !conn) continue;
      const count = ids.reduce((n, id) => n + this.scene.foldedCount(id), 0);
      // branches from the same point still open: the counter's click folds them too
      const open = this.expandedByPoint(b.from).get(key) ?? [];
      const r = Math.max(9, 4 + String(count).length * 3.6);
      const stub = cam.worldToScreen(b.start.x, b.start.y);
      out.push({ id: ids[0], ids: [...ids, ...open], screen: this.knobOutward(from, b.start, 10 + r), stub, r, count, collapsed: !open.length, hot: this.knobHover === ids[0], color: conn.style.stroke?.color ?? '#64748b' });
    }
    if (this.tool.kind !== 'select' || this.drag || !this.knobLatch) return out;
    for (const k of this.expandedSpots(this.knobLatch)) {
      out.push({ id: k.id, ids: k.ids, screen: k.screen, r: k.r, count: 0, collapsed: false, hot: this.knobHover === k.id, color: k.color });
    }
    return out;
  }

  private hitKnob(p: Vec2): ReturnType<Editor['foldKnobs']>[number] | null {
    let best: ReturnType<Editor['foldKnobs']>[number] | null = null;
    let bestD = Infinity;
    for (const k of this.foldKnobs()) {
      const d = Math.hypot(k.screen.x - p.x, k.screen.y - p.y);
      // the expanded ring is small and lies on the connector; once the pointer is on it, a wider margin holds the click
      const slop = k.stub ? k.r + 3 : k.r + (k.id === this.knobHover ? 12 : 6);
      if (d <= slop && d < bestD) {
        best = k;
        bestD = d;
      }
    }
    return best;
  }

  // ---------- connected shapes (points around the selected shape) ----------

  /** The single selected shape that shows the "add a connected shape" points, or null. */
  quickSource(): ObjectRecord | null {
    if (this.readOnly || this.tool.kind !== 'select' || this.sel.size !== 1 || this.editingId || this.pathEditId || this.spaceDown) return null;
    const o = this.doc.get([...this.sel][0]);
    if (!o || o.locked || Math.abs(o.rotation) > 1e-9) return null;
    if (o.type.startsWith('core:') ? !QUICK_TYPES.has(o.type) : !this.host.createDefaults || !this.caps(o).move) return null;
    return o;
  }

  /** Screen positions of the points: outside the middle of every side. */
  quickHandles(): { dir: QuickDir; screen: Vec2 }[] {
    const o = this.quickSource();
    if (!o) return [];
    const cam = this.renderer.camera;
    const c = cam.worldToScreen(o.x + o.w / 2, o.y + o.h / 2);
    const hw = (o.w * cam.zoom) / 2;
    const hh = (o.h * cam.zoom) / 2;
    if (hw < 6 && hh < 6) return [];
    return [
      { dir: 'up', screen: { x: c.x, y: c.y - hh - QUICK_OFFSET } },
      { dir: 'right', screen: { x: c.x + hw + QUICK_OFFSET, y: c.y } },
      { dir: 'down', screen: { x: c.x, y: c.y + hh + QUICK_OFFSET } },
      { dir: 'left', screen: { x: c.x - hw - QUICK_OFFSET, y: c.y } },
    ];
  }

  private hitQuick(p: Vec2): QuickDir | null {
    for (const h of this.quickHandles()) if (Math.hypot(h.screen.x - p.x, h.screen.y - p.y) <= QUICK_HIT) return h.dir;
    return null;
  }

  /** Point last seen under the pointer (redraws only when it changes). */
  private quickHoverShown: QuickDir | null = null;

  /** The point under the pointer (or being dragged from), for the overlay. */
  get quickHover(): QuickDir | null {
    const d = this.drag;
    if (d?.kind === 'quick') return d.dir;
    return d ? null : this.hitQuick(this.lastPointer);
  }

  /** Gap to the new shape: as to a shape already linked on that side, otherwise in proportion to the shape. */
  private quickGap(src: ObjectRecord, dir: QuickDir): number {
    for (const c of connectorsAttachedTo(this.doc, new Set([src.id]))) {
      const s = c.props['start'] as ConnectorEnd | undefined;
      const e = c.props['end'] as ConnectorEnd | undefined;
      const other = s?.objectId === src.id ? e?.objectId : s?.objectId;
      const o = other && other !== src.id ? this.doc.get(other) : undefined;
      if (!o || isLineLike(o)) continue;
      const gap = dir === 'right' ? o.x - (src.x + src.w) : dir === 'left' ? src.x - (o.x + o.w) : dir === 'down' ? o.y - (src.y + src.h) : src.y - (o.y + o.h);
      if (gap > 0 && gap < 4 * Math.max(src.w, src.h)) return gap;
    }
    return Math.max(src.w, src.h) * 0.45;
  }

  /**
   * Box of the new shape: next to the source on that side, moved sideways past shapes already there
   * (a fan of branches); `at` — where a drag ended (centered there, lined up with the source when close).
   */
  quickRect(src: ObjectRecord, dir: QuickDir, at?: Vec2): Rect {
    const horiz = dir === 'left' || dir === 'right';
    if (at) {
      let cx = at.x;
      let cy = at.y;
      const scx = src.x + src.w / 2;
      const scy = src.y + src.h / 2;
      if (horiz && Math.abs(cy - scy) < src.h / 2) cy = scy;
      if (!horiz && Math.abs(cx - scx) < src.w / 2) cx = scx;
      return { x: cx - src.w / 2, y: cy - src.h / 2, w: src.w, h: src.h };
    }
    const gap = this.quickGap(src, dir);
    const x0 = dir === 'right' ? src.x + src.w + gap : dir === 'left' ? src.x - gap - src.w : src.x;
    const y0 = dir === 'down' ? src.y + src.h + gap : dir === 'up' ? src.y - gap - src.h : src.y;
    const step = (horiz ? src.h : src.w) + gap * 0.5;
    const pad = gap * 0.2;
    const busy = (r: Rect) =>
      this.scene.query({ x: r.x - pad, y: r.y - pad, w: r.w + pad * 2, h: r.h + pad * 2 }).some((id) => {
        const o = this.doc.get(id);
        return !!o && id !== src.id && !isLineLike(o) && o.type !== CoreTypes.frame && o.type !== CoreTypes.group;
      });
    const first = { x: x0, y: y0, w: src.w, h: src.h };
    for (let i = 0; i < 12; i++) {
      const off = i === 0 ? 0 : (i % 2 ? 1 : -1) * Math.ceil(i / 2) * step;
      const r = horiz ? { ...first, y: y0 + off } : { ...first, x: x0 + off };
      if (!busy(r)) return r;
    }
    return first;
  }

  /** Arrows from this shape look like the last one already drawn from (or to) it. */
  private quickLinkStyle(src: ObjectRecord): { style?: Partial<Style>; route?: string; arrowEnd: boolean; arrowStart: boolean } {
    const last = connectorsAttachedTo(this.doc, new Set([src.id])).sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0)).pop();
    // capsules make mind maps: smooth branches without arrowheads
    const pill = src.type === CoreTypes.rect && (src.style.cornerRadius ?? 0) >= Math.min(src.w, src.h) / 2;
    if (!last) return pill ? { route: 'curved', arrowEnd: false, arrowStart: false } : { arrowEnd: true, arrowStart: false };
    return { style: JSON.parse(JSON.stringify(last.style)) as Partial<Style>, route: (last.props['route'] as string) ?? 'orthogonal', arrowEnd: last.props['arrowEnd'] !== false, arrowStart: last.props['arrowStart'] === true };
  }

  /** Props of the new shape: the source's without its text (a module type: its own defaults). */
  private quickProps(src: ObjectRecord): Record<string, unknown> {
    const props: Record<string, unknown> = JSON.parse(JSON.stringify(src.props));
    delete props['text'];
    delete props['spans'];
    return props;
  }

  /**
   * Add a shape like the selected one on a side, joined by an arrow (one undo step); it gets selected
   * and its text is typed right away. `at` — a point the shape is centered on instead of the free spot.
   */
  async quickAdd(dir: QuickDir, at?: Vec2): Promise<Id | undefined> {
    const src = this.quickSource();
    if (!src) return undefined;
    const rect = this.quickRect(src, dir, at);
    let props = this.quickProps(src);
    if (!src.type.startsWith('core:')) {
      try {
        props = await this.host.createDefaults!(src.type, { w: rect.w, h: rect.h });
      } catch (e) {
        this.reportError(e);
        return undefined;
      }
    }
    const link = this.quickLinkStyle(src);
    const id = this.edit('create', (tx) => {
      const o = makeObject(this.doc.canvasId, {
        type: src.type,
        ...rect,
        z: cmd.nextZ(this.doc),
        props,
        style: JSON.parse(JSON.stringify(src.style)) as Partial<Style>,
        formatVersion: src.formatVersion,
      });
      tx.create(src.keepRatio ? { ...o, keepRatio: true } : o);
      cmd.assignFrames(tx, [o.id]);
      const a = sideAnchor(src.type, dir);
      const b = sideAnchor(src.type, OPPOSITE[dir]);
      this.connectorIn(
        tx,
        { objectId: src.id, anchor: a, point: localToWorld(src, { x: a.x * src.w, y: a.y * src.h }) },
        { objectId: o.id, anchor: b, point: localToWorld(o, { x: b.x * o.w, y: b.y * o.h }) },
        link,
      );
      return o.id;
    });
    if (!id) return undefined;
    this.select([id]);
    const made = this.doc.get(id);
    if (made && made.type.startsWith('core:') && this.caps(made).editText) this.startTextEdit(id);
    return id;
  }

  /** An arrow from a side of the selected shape to an existing object; the object gets selected. */
  private quickConnect(srcId: Id, dir: QuickDir, end: ConnectorEnd): void {
    const src = this.doc.get(srcId);
    if (!src || !end.objectId) return;
    const a = sideAnchor(src.type, dir);
    const link = this.quickLinkStyle(src);
    const id = this.edit('create', (tx) => this.connectorIn(tx, { objectId: src.id, anchor: a, point: localToWorld(src, { x: a.x * src.w, y: a.y * src.h }) }, end, link));
    if (id) this.select([this.doc.get(end.objectId) && !isLineLike(this.doc.get(end.objectId)!) ? end.objectId : id]);
  }

  /** What a point would add, drawn faintly: the shape (null when the arrow goes to an existing object) and the arrow. */
  get quickPreview(): { rect: Rect | null; shape: ObjectRecord | null; arrow: ObjectRecord | null } | null {
    const d = this.drag;
    const dir = this.quickHover;
    const src = d?.kind === 'quick' ? this.doc.get(d.src) : this.quickSource();
    if (!src || !dir) return null;
    const cid = this.doc.canvasId;
    const a = sideAnchor(src.type, dir);
    const start: ConnectorEnd = { objectId: src.id, anchor: a, point: localToWorld(src, { x: a.x * src.w, y: a.y * src.h }) };
    const link = this.quickLinkStyle(src);
    const style = { ...(link.style ?? this.scaledStyle(CoreTypes.connector) ?? {}) };
    if (d?.kind === 'quick' && d.moved && d.bind) return { rect: null, shape: null, arrow: this.connectorGhost(start, d.bind, link.route ?? 'orthogonal', style, link) };
    const rect = this.quickRect(src, dir, d?.kind === 'quick' && d.moved ? d.cur : undefined);
    const b = sideAnchor(src.type, OPPOSITE[dir]);
    const end: ConnectorEnd = { point: { x: rect.x + b.x * rect.w, y: rect.y + b.y * rect.h } };
    const shape = src.type.startsWith('core:')
      ? makeObject(cid, { type: src.type, ...rect, z: 'a0', props: this.quickProps(src), style: { ...src.style, opacity: (src.style.opacity ?? 1) * 0.4 } })
      : null;
    return { rect, shape, arrow: this.connectorGhost(start, end, link.route ?? 'orthogonal', { ...style, opacity: 0.5 }, link) };
  }

  /** A connector that is not in the document (previews), routed like a real one. */
  private connectorGhost(start: ConnectorEnd, end: ConnectorEnd, route: string, style: Partial<Style> | undefined, arrows: { arrowEnd: boolean; arrowStart: boolean }): ObjectRecord {
    const unit = Math.max(0.05, (style?.stroke?.width ?? 2) / 2);
    const r = computeRoute(this.doc, start, end, route, (q) => this.scene.query(q), undefined, unit);
    const box = pointsBox(r.pts);
    return makeObject(this.doc.canvasId, {
      type: CoreTypes.connector,
      x: box.x,
      y: box.y,
      w: box.w,
      h: box.h,
      z: 'a0',
      style,
      props: { start: { ...start, point: r.a }, end: { ...end, point: r.b }, route, arrowEnd: arrows.arrowEnd, arrowStart: arrows.arrowStart, path: box.local },
    });
  }

  // ---------- arrange as a scheme ----------

  /** Shapes of the selection a scheme is built from, and the arrows joining two of them. */
  arrangeTargets(): { nodes: ObjectRecord[]; links: ObjectRecord[] } {
    const nodes = this.selection
      .map((id) => this.doc.get(id))
      .filter((o): o is ObjectRecord => !!o && !o.locked && !isLineLike(o) && o.type !== CoreTypes.pen && o.type !== CoreTypes.frame);
    const ids = new Set(nodes.map((o) => o.id));
    const links = connectorsAttachedTo(this.doc, ids).filter((c) => {
      const s = c.props['start'] as ConnectorEnd | undefined;
      const e = c.props['end'] as ConnectorEnd | undefined;
      return !!s?.objectId && !!e?.objectId && ids.has(s.objectId) && ids.has(e.objectId) && s.objectId !== e.objectId;
    });
    return { nodes, links };
  }

  canArrange(): boolean {
    if (this.readOnly) return false;
    const t = this.arrangeTargets();
    return t.nodes.length >= 2 && t.links.length > 0;
  }

  /**
   * Place the selected shapes and the arrows between them as a scheme going `direction`
   * (the DSkhema layout; sizes stay, the first shape stays put). One undo step.
   */
  arrangeFlow(direction: Direction): void {
    const { nodes, links } = this.arrangeTargets();
    if (this.readOnly || nodes.length < 2 || !links.length) return;
    if (!this.checkConflict(cmd.withContents(this.doc, nodes.map((o) => o.id)), 'move')) return;
    const shape = (o: ObjectRecord) => (o.type === CoreTypes.diamond ? 'diamond' : o.type === CoreTypes.ellipse ? 'ellipse' : 'rect') as 'diamond' | 'ellipse' | 'rect';
    const route = links.every((c) => c.props['route'] === 'curved') ? 'curved' : 'orthogonal';
    const plan = planArrange(
      nodes.map((o) => ({ id: o.id, x: o.x, y: o.y, w: o.w, h: o.h, shape: shape(o) })),
      links.map((c) => ({ id: c.id, from: (c.props['start'] as ConnectorEnd).objectId!, to: (c.props['end'] as ConnectorEnd).objectId!, label: typeof c.props['label'] === 'string' ? (c.props['label'] as string) : '' })),
      direction,
      { scale: 1 / this.naturalZoom(nodes), measure: diagramMeasure, curved: route === 'curved' },
    );
    this.edit('arrange', (tx) => {
      const moved = new Set<Id>();
      for (const o of nodes) {
        const to = plan.moves.get(o.id);
        if (!to) continue;
        const dx = to.x - o.x;
        const dy = to.y - o.y;
        for (const id of cmd.withContents(this.doc, [o.id])) {
          const m = this.doc.get(id)!;
          if (dx || dy) tx.update(id, { x: m.x + dx, y: m.y + dy });
          moved.add(id);
        }
      }
      cmd.assignFrames(tx, nodes.map((o) => o.id));
      this.rerouteFor(tx, moved);
      // the scheme's own routes and label spots; they re-route like any arrow once a shape moves
      for (const l of plan.links) {
        const c = this.doc.get(l.id);
        if (!c || (c.props['route'] ?? 'orthogonal') !== route) continue;
        const pts: Vec2[] = [];
        for (let i = 0; i < l.points.length; i += 2) pts.push({ x: l.points[i], y: l.points[i + 1] });
        const box = pointsBox(pts);
        const s = c.props['start'] as ConnectorEnd;
        const e = c.props['end'] as ConnectorEnd;
        tx.update(c.id, {
          x: box.x,
          y: box.y,
          w: Math.max(box.w, 0.01),
          h: Math.max(box.h, 0.01),
          rotation: 0,
          props: {
            ...c.props,
            start: { objectId: s.objectId, anchor: l.fromAnchor, point: pts[0] },
            end: { objectId: e.objectId, anchor: l.toAnchor, point: pts[pts.length - 1] },
            path: box.local,
            ...(l.labelAt !== undefined ? { labelAt: l.labelAt } : {}),
          },
        });
      }
    });
    this.renderer.invalidate();
  }

  /**
   * Binding target under a world point: objects (glued to their frame), connectors and
   * lines (a point on the path). Frames bind only near their border; groups never.
   */
  bindAt(w: Vec2, exclude?: Set<Id>): ConnectorEnd | null {
    const zoom = this.renderer.camera.zoom;
    const hit = this.scene.hitTest(w, 6 / zoom, (o) => {
      if (exclude?.has(o.id) || o.type === CoreTypes.group) return false;
      if (o.type === CoreTypes.frame) {
        const l = worldToLocal(o, w);
        const edge = Math.min(Math.abs(l.x), Math.abs(l.y), Math.abs(o.w - l.x), Math.abs(o.h - l.y));
        return edge <= 10 / zoom;
      }
      return true;
    });
    const target = hit ? this.doc.get(hit) : undefined;
    return target ? bindTo(target, w) : null;
  }

  /** Current snap preview while drawing or re-attaching connector ends (for the overlay). */
  get bindPreview(): ConnectorEnd | null {
    const d = this.drag;
    if (d?.kind === 'line') return d.endBind ?? null;
    if (d?.kind === 'endpoint' || d?.kind === 'quick') return d.bind ?? null;
    return null;
  }

  /** World endpoints of a selected connector/line (for handles). */
  lineEnds(o: ObjectRecord): [Vec2, Vec2] | null {
    const pts = worldPath(o);
    if (pts.length < 4) return null;
    return [
      { x: pts[0], y: pts[1] },
      { x: pts[pts.length - 2], y: pts[pts.length - 1] },
    ];
  }

  // ---------- bezier pen ----------

  /** Path being placed with the pen tool: world nodes [x, y, inX, inY, outX, outY]*; `dragging` = node whose handles follow the pointer. */
  private pathDraft: { nodes: number[]; dragging: number; cursor: Vec2; extend?: Id } | null = null;

  /** Read-only view of the path being placed (for the overlay). */
  get pathDraftState(): Readonly<{ nodes: number[]; dragging: number; cursor: Vec2 }> | null {
    return this.pathDraft;
  }

  /** Is the pointer over the first node, so a click would close the path? */
  get pathCanClose(): boolean {
    const d = this.pathDraft;
    if (!d || d.nodes.length < 12 || d.dragging >= 0) return false;
    return Math.hypot(d.cursor.x - d.nodes[0], d.cursor.y - d.nodes[1]) * this.renderer.camera.zoom <= 8;
  }

  private pathDown(w: Vec2): void {
    if (this.pathCanClose) {
      this.finishPath(true);
      return;
    }
    if (!this.pathDraft && this.continuePathAt(w)) return;
    const p = this.snapPoint(w);
    if (!this.pathDraft) this.pathDraft = { nodes: [], dragging: -1, cursor: p };
    const d = this.pathDraft;
    d.nodes.push(p.x, p.y, p.x, p.y, p.x, p.y);
    d.dragging = d.nodes.length / 6 - 1;
    d.cursor = p;
    this.renderer.invalidate();
  }

  /** Pen press on an end point of an open path: keep drawing that path (Photoshop behaviour). */
  private continuePathAt(w: Vec2): boolean {
    const tol = 8 / this.renderer.camera.zoom;
    for (const id of this.scene.hitTestAll(w, tol)) {
      const o = this.doc.get(id);
      if (!o || o.type !== CoreTypes.path || o.locked || o.props['closed'] === true) continue;
      const local = (o.props['nodes'] as number[] | undefined) ?? [];
      const n = nodeCount(local);
      if (n < 2) continue;
      const world: number[] = [];
      for (let i = 0; i < local.length; i += 2) {
        const q = localToWorld(o, { x: local[i], y: local[i + 1] });
        world.push(q.x, q.y);
      }
      const atEnd = Math.hypot(world[(n - 1) * 6] - w.x, world[(n - 1) * 6 + 1] - w.y) <= tol;
      const atStart = Math.hypot(world[0] - w.x, world[1] - w.y) <= tol;
      if (!atEnd && !atStart) continue;
      let nodes = world;
      if (!atEnd) {
        // continue from the first point: walk the path backwards (in and out handles swap)
        nodes = [];
        for (let i = n - 1; i >= 0; i--) {
          const b = i * 6;
          nodes.push(world[b], world[b + 1], world[b + 4], world[b + 5], world[b + 2], world[b + 3]);
        }
      }
      this.pathDraft = { nodes, dragging: -1, cursor: w, extend: o.id };
      this.renderer.invalidate();
      return true;
    }
    return false;
  }

  private pathMove(w: Vec2, pressed: boolean, alt: boolean): void {
    const d = this.pathDraft!;
    d.cursor = w;
    if (pressed && d.dragging >= 0) {
      // dragging out of a new node pulls symmetric handles (Alt: only the outgoing one, a cusp)
      const i = d.dragging * 6;
      const x = d.nodes[i];
      const y = d.nodes[i + 1];
      d.nodes[i + 4] = w.x;
      d.nodes[i + 5] = w.y;
      if (!alt) {
        d.nodes[i + 2] = 2 * x - w.x;
        d.nodes[i + 3] = 2 * y - w.y;
      }
    }
    this.renderer.invalidate();
  }

  private pathUndoNode(): void {
    const d = this.pathDraft;
    if (!d) return;
    d.nodes.length = Math.max(0, d.nodes.length - 6);
    d.dragging = -1;
    if (!d.nodes.length) this.pathDraft = null;
    this.renderer.invalidate();
  }

  /** Object for a set of world nodes (shared by the live preview and creation). */
  private pathRecord(nodes: number[], closed: boolean, z?: string): ObjectRecord | null {
    const zoom = this.renderer.camera.zoom;
    const flat = flattenBezier(nodes, closed, 0.35 / zoom);
    if (flat.length < 4) return null;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let i = 0; i < flat.length; i += 2) {
      x0 = Math.min(x0, flat[i]);
      x1 = Math.max(x1, flat[i]);
      y0 = Math.min(y0, flat[i + 1]);
      y1 = Math.max(y1, flat[i + 1]);
    }
    const toLocal = (v: number, i: number) => (i % 2 === 0 ? v - x0 : v - y0);
    return makeObject(this.doc.canvasId, {
      type: CoreTypes.path,
      x: x0,
      y: y0,
      w: Math.max(x1 - x0, 0.01),
      h: Math.max(y1 - y0, 0.01),
      z: z ?? cmd.nextZ(this.doc),
      props: { nodes: nodes.map(toLocal), points: flat.map(toLocal), closed },
      style: { stroke: { color: this.pathStyle.color, width: parseFloat((this.pathStyle.width / zoom).toPrecision(3)), dash: 'solid' }, fill: { kind: 'none' }, opacity: 1 },
    });
  }

  /** Path preview: placed nodes plus a rubber-band segment to the pointer. */
  private pathGhost(): ObjectRecord | null {
    const d = this.pathDraft;
    if (!d) return null;
    const nodes = d.nodes.slice();
    const closing = this.pathCanClose;
    if (d.dragging < 0 && !closing) nodes.push(d.cursor.x, d.cursor.y, d.cursor.x, d.cursor.y, d.cursor.x, d.cursor.y);
    return this.pathRecord(nodes, closing, 'a0');
  }

  /** Commit the path being placed (closed: the last segment returns to the first node). */
  /** Finish the pen's path; then the select tool takes over (unless another tool is being picked). */
  finishPath(closed: boolean, toSelect = true): void {
    const d = this.pathDraft;
    this.pathDraft = null;
    this.renderer.invalidate();
    if (toSelect && this.tool.kind === 'path') this.setTool('select');
    if (!d || d.nodes.length < 12) return;
    const rec = this.pathRecord(d.nodes, closed);
    if (!rec) return;
    const ext = d.extend ? this.doc.get(d.extend) : undefined;
    if (ext) {
      // the continued path stays the same object (style, links, layer); the geometry is replaced
      this.edit('draw', (tx) => tx.update(ext.id, { x: rec.x, y: rec.y, w: rec.w, h: rec.h, rotation: 0, props: { ...ext.props, nodes: rec.props['nodes'], points: rec.props['points'], closed } }));
      this.select([ext.id]);
      return;
    }
    const id = this.edit('draw', (tx) => {
      const created = tx.create(rec);
      cmd.assignFrames(tx, [created.id]);
      return created.id;
    });
    if (id) this.select([id]);
  }

  // ---------- DSkhema diagrams ----------

  /** A single selected diagram keeps its proportions when resized (it scales as a picture). */
  /** Diagrams always keep their proportions; other objects when the user asked for it (`keepRatio`). */
  private ratioLocked(): boolean {
    if (!this.sel.size) return false;
    if (this.sel.size === 1 && this.doc.get([...this.sel][0])?.type === CoreTypes.diagram) return true;
    return [...this.sel].every((id) => this.doc.get(id)?.keepRatio === true);
  }

  private diagramCtx() {
    const c = this.scene.displayCtx;
    return { shapes: c.diagramShapes, locale: c.locale };
  }

  /** Create a diagram from source; natural size at the current zoom, centered on `at` (view center by default). */
  createDiagram(source: string, at?: Vec2): Id | undefined {
    if (this.readOnly) return undefined;
    const nat = diagramNaturalSize(source, this.diagramCtx());
    const k = this.viewScale;
    const c = at ?? { x: this.renderer.camera.cx, y: this.renderer.camera.cy };
    const w = nat.w * k;
    const h = nat.h * k;
    let x = c.x - w / 2;
    const y = c.y - h / 2;
    if (!at) {
      // no explicit spot: move right until the place is free, so schemes never land on each other
      const gap = 40 * k;
      for (let i = 0; i < 40; i++) {
        const hits = this.scene.query({ x: x - gap / 2, y: y - gap / 2, w: w + gap, h: h + gap });
        if (!hits.length) break;
        x = Math.max(...hits.map((hid) => {
          const b = this.scene.index.bounds(hid);
          return b ? b.x + b.w : x;
        })) + gap;
      }
      const view = this.renderer.camera.visibleRect();
      if (x + w > view.x + view.w || x < view.x) this.reveal(() => this.renderer.camera.set({ cx: x + w / 2, cy: y + h / 2 }));
    }
    const id = this.edit('create', (tx) => {
      const o = makeObject(this.doc.canvasId, { type: CoreTypes.diagram, x, y, w, h, z: cmd.nextZ(this.doc), props: { source, lang: 'dskhema', langVersion: LANG_VERSION } });
      tx.create(o);
      cmd.assignFrames(tx, [o.id]);
      this.rerouteFor(tx, new Set([o.id]));
      return o.id;
    });
    if (id) this.select([id]);
    return id;
  }

  /** Replace a diagram's source; it keeps its current scale and top-left corner. */
  setDiagramSource(id: Id, source: string): void {
    const o = this.doc.get(id);
    if (!o || o.type !== CoreTypes.diagram || o.locked) return;
    const before = diagramNaturalSize((o.props['source'] as string) ?? '', this.diagramCtx());
    const after = diagramNaturalSize(source, this.diagramCtx());
    const k = Math.min(o.w / before.w, o.h / before.h) || 1;
    this.edit('diagram', (tx) => {
      tx.update(id, { w: after.w * k, h: after.h * k, props: { ...o.props, source, lang: 'dskhema', langVersion: LANG_VERSION } });
      this.rerouteFor(tx, new Set([id]));
    });
  }

  /** Direction of a flowchart diagram; null for other objects and kinds. */
  diagramDirection(id: Id): Direction | null {
    const o = this.doc.get(id);
    return o?.type === CoreTypes.diagram ? flowDirection((o.props['source'] as string) ?? '') : null;
  }

  /** Turn a flowchart diagram to another direction (its header changes; scale and top-left corner stay). */
  setDiagramDirection(id: Id, dir: Direction): void {
    const o = this.doc.get(id);
    if (!o || o.type !== CoreTypes.diagram) return;
    const source = (o.props['source'] as string) ?? '';
    const next = withFlowDirection(source, dir);
    if (next !== source) this.setDiagramSource(id, next);
  }

  /** Back to 100% of the diagram's natural size (top-left corner stays). */
  resetDiagramSize(id: Id): void {
    const o = this.doc.get(id);
    if (!o || o.type !== CoreTypes.diagram || o.locked) return;
    const nat = diagramNaturalSize((o.props['source'] as string) ?? '', this.diagramCtx());
    this.edit('resize', (tx) => tx.update(id, { w: nat.w, h: nat.h }));
  }

  /**
   * Replace a diagram with ordinary objects inside a frame: shapes, glued connectors, texts
   * (docs/ru/DSKHEMA.md §7). One undo step; the frame is selected afterwards.
   */
  explodeDiagram(id: Id, frameTitle = ''): Id | undefined {
    const o = this.doc.get(id);
    if (!o || o.type !== CoreTypes.diagram || o.locked || this.readOnly) return undefined;
    if (!isSupportedDiagramVersion(o.props['langVersion'])) {
      this.host.notify('warning', this.host.t('diagram.explodeOutdated'));
      return undefined;
    }
    const { geometry } = layoutDiagram((o.props['source'] as string) ?? '', this.diagramCtx());
    if (!geometry) return undefined;
    const fit = diagramFit(geometry, o.w, o.h);
    const plan = planExplode(geometry, { x: o.x + fit.dx, y: o.y + fit.dy }, fit.s);
    const frameId = this.edit('explode', (tx) => {
      const frame = makeObject(this.doc.canvasId, { type: CoreTypes.frame, x: o.x, y: o.y, w: o.w, h: o.h, z: cmd.nextZ(this.doc), props: { title: frameTitle || o.name || '' } });
      tx.create(frame);
      const ids = new Map<string, Id>();
      for (const p of plan.objects) {
        const formatVersion = p.type.startsWith('core:') ? undefined : this.host.typeFormatVersion?.(p.type);
        const props = p.moduleDefaults ? { ...(this.scene.displayCtx.moduleDefaults?.(p.type) ?? {}), ...p.props } : p.props;
        const rec = makeObject(this.doc.canvasId, { type: p.type, x: p.x, y: p.y, w: p.w, h: p.h, z: cmd.nextZ(this.doc), style: p.style, props, formatVersion });
        tx.create({ ...rec, frameId: frame.id });
        ids.set(p.key, rec.id);
      }
      for (const c of plan.connectors) {
        const a = ids.get(c.from);
        const b = ids.get(c.to);
        const ao = a ? this.doc.get(a) : undefined;
        const bo = b ? this.doc.get(b) : undefined;
        if (!ao || !bo) continue;
        const start = { objectId: ao.id, ...(c.fromAnchor ? { anchor: c.fromAnchor } : {}), point: { x: ao.x + ao.w / 2, y: ao.y + ao.h / 2 } };
        const end = { objectId: bo.id, ...(c.toAnchor ? { anchor: c.toAnchor } : {}), point: { x: bo.x + bo.w / 2, y: bo.y + bo.h / 2 } };
        // the scheme's own route and label spot; the connector re-routes once one of its shapes moves
        const pts: Vec2[] = [];
        for (let i = 0; i < c.points.length; i += 2) pts.push({ x: c.points[i], y: c.points[i + 1] });
        const box = pointsBox(pts);
        start.point = pts[0];
        end.point = pts[pts.length - 1];
        const conn = makeObject(this.doc.canvasId, {
          type: CoreTypes.connector,
          x: box.x,
          y: box.y,
          w: Math.max(box.w, 0.01),
          h: Math.max(box.h, 0.01),
          z: cmd.nextZ(this.doc),
          style: c.style,
          // drawn like the scheme's own links (angular or smooth), still glued to the shapes
          props: { start, end, route: plan.route, arrowStart: c.arrowStart, arrowEnd: c.arrowEnd, label: c.label, ...(c.labelAt !== undefined ? { labelAt: c.labelAt } : {}), path: box.local },
        });
        tx.create({ ...conn, frameId: frame.id });
      }
      cmd.deleteDeep(tx, [id]);
      return frame.id;
    });
    if (frameId) this.select([frameId]);
    return frameId;
  }

  // ---------- bezier path node editing (direct selection) ----------

  private pathEditId: Id | null = null;
  /** Selected node of the edited path (-1: none). */
  private pathEditSel = -1;
  /** Where a click would add a point (local coords of the edited path), for the overlay. */
  private pathHover: Vec2 | null = null;

  /** Edited path, selected node and the "add point here" marker (for the overlay). */
  get pathEditState(): { obj: ObjectRecord; selected: number; hover: Vec2 | null } | null {
    const o = this.pathEditId ? this.doc.get(this.pathEditId) : undefined;
    return o && o.type === CoreTypes.path ? { obj: o, selected: this.pathEditSel, hover: this.pathHover } : null;
  }

  enterPathEdit(id: Id): void {
    if (this.readOnly) return;
    if (this.tool.kind !== 'select') this.setTool('select');
    this.select([id]);
    this.pathEditId = id;
    this.pathEditSel = -1;
    this.pathHover = null;
    this.emit('pathedit', id);
    this.renderer.invalidate();
  }

  exitPathEdit(): void {
    if (!this.pathEditId) return;
    this.pathEditId = null;
    this.pathEditSel = -1;
    this.pathHover = null;
    this.emit('pathedit', null);
    this.updateCursor();
    this.renderer.invalidate();
  }

  private pathEditHit(w: Vec2): { kind: 'part'; index: number; part: NodePart } | { kind: 'segment'; seg: number; t: number; point: Vec2 } | null {
    const o = this.pathEditId ? this.doc.get(this.pathEditId) : undefined;
    if (!o) return null;
    const nodes = (o.props['nodes'] as number[] | undefined) ?? [];
    const l = worldToLocal(o, w);
    const tol = 7 / this.renderer.camera.zoom;
    const part = hitNodePart(nodes, l, tol, this.pathEditSel);
    if (part) return { kind: 'part', ...part };
    const near = nearestOnPath(nodes, o.props['closed'] === true, l);
    if (near && near.dist <= tol) return { kind: 'segment', seg: near.seg, t: near.t, point: near.point };
    return null;
  }

  /** Press in node-edit mode; returns false when the press is outside the path (edit mode ends). */
  private pathEditDown(w: Vec2, alt: boolean): boolean {
    const o = this.pathEditId ? this.doc.get(this.pathEditId) : undefined;
    if (!o || o.locked) {
      this.exitPathEdit();
      return false;
    }
    const hit = this.pathEditHit(w);
    if (!hit) {
      this.exitPathEdit();
      return false;
    }
    const nodes = (o.props['nodes'] as number[] | undefined) ?? [];
    if (hit.kind === 'segment') {
      // click on the outline adds a point there (the shape does not change) and grabs it
      const ins = insertNode(nodes, hit.seg, hit.t);
      this.edit('add point', (tx) => this.applyPathNodes(tx, o, ins.nodes));
      this.pathEditSel = ins.index;
      this.pathHover = null;
      const cur = this.doc.get(o.id)!;
      this.beginNodeDrag(cur, ins.index, 'anchor', w);
      return true;
    }
    this.pathEditSel = hit.index;
    this.beginNodeDrag(o, hit.index, alt && hit.part === 'anchor' ? 'convert' : hit.part, w);
    return true;
  }

  private beginNodeDrag(o: ObjectRecord, index: number, part: NodePart | 'convert', w: Vec2): void {
    try {
      const tx = this.doc.begin({ label: 'edit path', source: { kind: 'user' } });
      const start = worldToLocal(o, w);
      this.drag = { kind: 'node', tx, base: o, orig: ((o.props['nodes'] as number[] | undefined) ?? []).slice(), index, part, start, moved: false };
    } catch (err) {
      this.reportError(err);
    }
    this.renderer.invalidate();
  }

  private deletePathNode(): void {
    const o = this.pathEditId ? this.doc.get(this.pathEditId) : undefined;
    if (!o || this.pathEditSel < 0) return;
    const nodes = (o.props['nodes'] as number[] | undefined) ?? [];
    const min = o.props['closed'] === true ? 3 : 2;
    if (nodeCount(nodes) <= min) return;
    const next = removeNode(nodes, this.pathEditSel);
    this.edit('delete point', (tx) => this.applyPathNodes(tx, o, next));
    this.pathEditSel = Math.min(this.pathEditSel, nodeCount(next) - 1);
    this.renderer.invalidate();
  }

  /**
   * Write new local nodes of a path (in `base`'s frame): re-flatten, refit the box to the outline
   * and move it so nothing shifts on screen, rotation included.
   */
  private applyPathNodes(tx: TxContext, base: ObjectRecord, nodes: number[]): void {
    const closed = base.props['closed'] === true;
    const flat = flattenBezier(nodes, closed, 0.35 / this.renderer.camera.zoom);
    if (flat.length < 4) return;
    let mx = Infinity, my = Infinity, Mx = -Infinity, My = -Infinity;
    for (let i = 0; i < flat.length; i += 2) {
      mx = Math.min(mx, flat[i]);
      Mx = Math.max(Mx, flat[i]);
      my = Math.min(my, flat[i + 1]);
      My = Math.max(My, flat[i + 1]);
    }
    const w = Math.max(Mx - mx, 0.01);
    const h = Math.max(My - my, 0.01);
    // new center = old center + R · (shift of the box center in local coordinates)
    const ox = mx + w / 2 - base.w / 2;
    const oy = my + h / 2 - base.h / 2;
    const c = Math.cos(base.rotation);
    const sn = Math.sin(base.rotation);
    const cx = base.x + base.w / 2 + ox * c - oy * sn;
    const cy = base.y + base.h / 2 + ox * sn + oy * c;
    const shift = (v: number, i: number) => (i % 2 === 0 ? v - mx : v - my);
    tx.update(base.id, { x: cx - w / 2, y: cy - h / 2, w, h, props: { ...base.props, nodes: nodes.map(shift), points: flat.map(shift) } });
  }

  // ---------- freehand drawing & eraser ----------

  /** Style of new freehand strokes. */
  penStyle = { ...DEFAULT_PEN_STYLE };
  /** Style of new bezier paths (the pen tool). */
  pathStyle = { ...DEFAULT_PATH_STYLE };
  /** Eraser radius in CSS px. */
  eraserRadius = 10;

  setPenStyle(patch: Partial<PenToolStyle>): void {
    this.penStyle = {
      color: patch.color ?? this.penStyle.color,
      width: clampPenWidth(patch.width ?? this.penStyle.width),
      brush: isBrush(patch.brush) ? patch.brush : this.penStyle.brush,
      mode: isStrokeMode(patch.mode) ? patch.mode : this.penStyle.mode,
    };
    this.emit('settings', this.settings);
  }

  setPathStyle(patch: Partial<PathToolStyle>): void {
    this.pathStyle = { ...this.pathStyle, ...patch, width: clampPenWidth(patch.width ?? this.pathStyle.width) };
    this.emit('settings', this.settings);
  }

  /** Pointer position in world coordinates (overlay cursor for the eraser). */
  get pointerWorld(): Vec2 {
    return this.renderer.camera.screenToWorld(this.lastPointer.x, this.lastPointer.y);
  }

  /** The stroke object a freehand drag turns into (shared by the live preview and creation). */
  private penRecord(raw: number[], z?: string, pressure?: number[], gesture = false): ObjectRecord {
    const zoom = this.renderer.camera.zoom;
    const done = finishStroke(raw, pressure, zoom, gesture ? 'light' : this.penStyle.mode);
    const pts = done.points;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let i = 0; i < pts.length; i += 2) {
      x0 = Math.min(x0, pts[i]);
      x1 = Math.max(x1, pts[i]);
      y0 = Math.min(y0, pts[i + 1]);
      y1 = Math.max(y1, pts[i + 1]);
    }
    const pad = (this.penStyle.width / zoom) * (gesture ? 0.6 : 0.7);
    x0 -= pad;
    y0 -= pad;
    x1 += pad;
    y1 += pad;
    const local = pts.map((v, i) => (i % 2 === 0 ? v - x0 : v - y0));
    const props: Record<string, unknown> = { points: local };
    if (!gesture) {
      props.brush = this.penStyle.brush;
      if (done.pressure) props.pressure = done.pressure;
    }
    return makeObject(this.doc.canvasId, {
      type: CoreTypes.pen,
      x: x0,
      y: y0,
      w: Math.max(x1 - x0, 0.01),
      h: Math.max(y1 - y0, 0.01),
      z: z ?? cmd.nextZ(this.doc),
      props,
      style: {
        stroke: { color: this.penStyle.color, width: this.penStyle.width / zoom, dash: 'solid' },
        fill: { kind: 'none' },
        opacity: 1,
      },
    });
  }

  private finishPen(d: Extract<Drag, { kind: 'pen' }>): void {
    const rec = this.penRecord(d.pts, undefined, d.pressure);
    // the tool stays active so several strokes can be drawn in a row
    this.edit('draw', (tx) => {
      const created = tx.create(rec);
      cmd.assignFrames(tx, [created.id]);
    });
    this.renderer.invalidate();
  }

  /** Erase everything the eraser touched between the last and current pointer positions (one undo step). */
  private eraseAlong(d: Extract<Drag, { kind: 'erase' }>, w: Vec2): void {
    const r = this.eraserRadius / this.renderer.camera.zoom;
    const dist = Math.hypot(w.x - d.last.x, w.y - d.last.y);
    const steps = Math.max(1, Math.ceil(dist / (r * 0.5)));
    for (let i = 0; i <= steps; i++) {
      const p = { x: d.last.x + ((w.x - d.last.x) * i) / steps, y: d.last.y + ((w.y - d.last.y) * i) / steps };
      for (const id of this.scene.hitTestAll(p, r)) {
        const o = this.doc.get(id);
        if (o && !o.locked) d.erased.add(id);
      }
    }
    d.last = w;
    this.renderer.invalidate();
  }

  /** Objects the eraser would remove: the ones swept while dragging, or the ones under it on hover. */
  get eraseMarked(): Id[] {
    if (this.tool.kind !== 'eraser') return [];
    const d = this.drag;
    if (d?.kind === 'erase') return [...d.erased];
    if (d) return [];
    const r = this.eraserRadius / this.renderer.camera.zoom;
    return this.scene.hitTestAll(this.pointerWorld, r).filter((id) => !this.doc.get(id)?.locked);
  }

  // ---------- smart guides ----------

  /** Bounds of nearby objects the moving selection can align to. */
  private guideTargets(moving: Set<Id>): Rect[] {
    const view = this.renderer.camera.visibleRect(200);
    const roots = new Set<Id>();
    for (const id of this.scene.query(view)) {
      const root = cmd.topAncestor(this.doc, id);
      if (moving.has(root) || moving.has(id)) continue;
      const o = this.doc.get(root);
      if (!o || o.type === CoreTypes.connector) continue;
      roots.add(root);
      if (roots.size >= 600) break;
    }
    return [...roots].map((id) => boxBounds(this.doc.get(id)!));
  }

  /** Snap a moved box to edges/centers of targets within a few screen pixels. */
  private snapToGuides(anchor: Rect, dx: number, dy: number, targets: Rect[], axisLock: boolean): { dx: number; dy: number; guides: Guide[]; gaps: GapGuide[] } {
    const tol = 6 / this.renderer.camera.zoom;
    const b = { x: anchor.x + dx, y: anchor.y + dy, w: anchor.w, h: anchor.h };
    const mx = [b.x, b.x + b.w / 2, b.x + b.w];
    const my = [b.y, b.y + b.h / 2, b.y + b.h];
    let bestX: { d: number; at: number; t: Rect } | null = null;
    let bestY: { d: number; at: number; t: Rect } | null = null;
    for (const t of targets) {
      const tx = [t.x, t.x + t.w / 2, t.x + t.w];
      const ty = [t.y, t.y + t.h / 2, t.y + t.h];
      for (const m of mx) for (const v of tx) {
        const dd = v - m;
        if (Math.abs(dd) <= tol && (!bestX || Math.abs(dd) < Math.abs(bestX.d))) bestX = { d: dd, at: v, t };
      }
      for (const m of my) for (const v of ty) {
        const dd = v - m;
        if (Math.abs(dd) <= tol && (!bestY || Math.abs(dd) < Math.abs(bestY.d))) bestY = { d: dd, at: v, t };
      }
    }
    const guides: Guide[] = [];
    const gaps: GapGuide[] = [];
    // equal spacing wins over an edge when it is closer
    const spaceX = equalSpacing(b, targets, 'x', tol);
    const spaceY = equalSpacing(b, targets, 'y', tol);
    if (spaceX && (!bestX || Math.abs(spaceX.d) < Math.abs(bestX.d)) && !(axisLock && dx === 0)) {
      dx += spaceX.d;
      bestX = null;
      gaps.push(...spaceX.gaps);
    } else if (bestX && !(axisLock && dx === 0)) dx += bestX.d;
    else bestX = null;
    if (spaceY && (!bestY || Math.abs(spaceY.d) < Math.abs(bestY.d)) && !(axisLock && dy === 0)) {
      dy += spaceY.d;
      bestY = null;
      gaps.push(...spaceY.gaps);
    } else if (bestY && !(axisLock && dy === 0)) dy += bestY.d;
    else bestY = null;
    const nb = { x: anchor.x + dx, y: anchor.y + dy, w: anchor.w, h: anchor.h };
    if (bestX) guides.push({ axis: 'x', at: bestX.at, from: Math.min(nb.y, bestX.t.y), to: Math.max(nb.y + nb.h, bestX.t.y + bestX.t.h) });
    if (bestY) guides.push({ axis: 'y', at: bestY.at, from: Math.min(nb.x, bestY.t.x), to: Math.max(nb.x + nb.w, bestY.t.x + bestY.t.w) });
    return { dx, dy, guides, gaps };
  }

  private beginEndpoint(h: 'p0' | 'p1'): void {
    const id = this.selection[0];
    const o = id ? this.doc.get(id) : undefined;
    if (!o || o.locked) return;
    let tx: TxContext;
    try {
      tx = this.doc.begin({ label: 'reconnect', source: { kind: 'user' } });
    } catch (err) {
      this.reportError(err);
      return;
    }
    this.drag = { kind: 'endpoint', tx, id, which: h === 'p0' ? 'start' : 'end', exclude: dependentConnectors(this.doc, id) };
  }

  private applyEndpoint(d: Extract<Drag, { kind: 'endpoint' }>, w: Vec2): void {
    const o = this.doc.get(d.id);
    if (!o) return;
    const bind = this.bindAt(w, d.exclude);
    d.bind = bind ?? undefined;
    this.bindHover = bind?.objectId ?? null;
    try {
      if (o.type === CoreTypes.connector) {
        d.tx.updateProps(d.id, { [d.which]: bind ?? { point: this.snapPoint(w) } });
        rerouteConnector(d.tx, this.doc.get(d.id)!, (r) => this.scene.query(r));
        this.rerouteFor(d.tx, new Set([d.id]));
      } else {
        // plain line: move the endpoint freely
        const ends = this.lineEnds(o);
        if (!ends) return;
        const p = bind ? bind.point : this.snapPoint(w);
        const a = d.which === 'start' ? p : ends[0];
        const b = d.which === 'start' ? ends[1] : p;
        const x0 = Math.min(a.x, b.x);
        const y0 = Math.min(a.y, b.y);
        d.tx.update(d.id, {
          x: x0,
          y: y0,
          w: Math.abs(b.x - a.x),
          h: Math.abs(b.y - a.y),
          rotation: 0,
          props: { ...o.props, points: [a.x - x0, a.y - y0, b.x - x0, b.y - y0] },
        });
        this.rerouteFor(d.tx, new Set([d.id]));
      }
    } catch (err) {
      this.cancelDrag();
      this.reportError(err);
    }
    this.renderer.invalidate();
  }

  private finishEndpoint(d: Extract<Drag, { kind: 'endpoint' }>): void {
    const o = this.doc.get(d.id);
    try {
      if (o && o.type === CoreTypes.line && d.bind) {
        // a line glued to an object becomes a routed connector, keeping its look
        const ends = this.lineEnds(o)!;
        const other: ConnectorEnd = { point: d.which === 'start' ? ends[1] : ends[0] };
        const start = d.which === 'start' ? d.bind : other;
        const end = d.which === 'start' ? other : d.bind;
        d.tx.delete(o.id);
        const c = makeObject(this.doc.canvasId, {
          type: CoreTypes.connector,
          x: o.x,
          y: o.y,
          w: o.w,
          h: o.h,
          z: o.z,
          style: o.style,
          props: { start, end, route: 'orthogonal', arrowEnd: !!o.props['arrowEnd'], arrowStart: !!o.props['arrowStart'], path: [0, 0, 1, 1] },
        });
        d.tx.create(c);
        rerouteConnector(d.tx, this.doc.get(c.id)!, (r) => this.scene.query(r));
        d.tx.commit();
        this.select([c.id]);
        return;
      }
      d.tx.commit();
    } catch (err) {
      if (d.tx.isOpen) d.tx.rollback();
      this.reportError(err);
    }
    this.renderer.invalidate();
  }

  private beginTransform(h: HandleId, w: Vec2): void {
    if (h === 'p0' || h === 'p1') return this.beginEndpoint(h);
    const box = this.selectionBox();
    if (!box) return;
    const roots = this.selection;
    const op = h === 'rot' ? 'rotate' : 'resize';
    const all = cmd.withDescendants(this.doc, roots);
    if (!this.checkConflict(all, op)) return;
    const orig = new Map<Id, ObjectRecord>();
    for (const id of all) orig.set(id, this.doc.get(id)!);
    let tx: TxContext;
    try {
      tx = this.doc.begin({ label: op, source: { kind: 'user' } });
    } catch (err) {
      this.reportError(err);
      return;
    }
    const single = roots.length === 1 && this.doc.get(roots[0])!.type !== CoreTypes.group;
    if (h === 'rot') {
      const center = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
      this.drag = { kind: 'rotate', tx, center, startAngle: Math.atan2(w.y - center.y, w.x - center.x), orig, single };
    } else {
      this.drag = { kind: 'resize', tx, handle: h, startWorld: w, box, orig, single, targets: this.settings.smartGuides && box.rotation === 0 ? this.guideTargets(new Set(all)) : [], guides: [] };
    }
  }

  private applyResize(d: Extract<Drag, { kind: 'resize' }>, w: Vec2, keepAspect: boolean, snapEdges = false): void {
    const box = d.box;
    // a text object pulled by a corner scales as a whole, its font included
    const scalesText = d.single && d.handle.length === 2 && [...d.orig.values()].some((o) => o.type === CoreTypes.text && !!o.style.text);
    if (scalesText) keepAspect = true;
    const l = worldToLocal(box, w);
    const l0 = worldToLocal(box, d.startWorld);
    const dx = l.x - l0.x;
    const dy = l.y - l0.y;
    const h = d.handle;
    // new local rect (in the unrotated box frame)
    let x0 = 0, y0 = 0, x1 = box.w, y1 = box.h;
    if (h.includes('w')) x0 += dx;
    if (h.includes('e')) x1 += dx;
    if (h.includes('n')) y0 += dy;
    if (h.includes('s')) y1 += dy;
    if (this.settings.snapToGrid && box.rotation === 0) {
      const st = this.settings.grid.step;
      if (h.includes('w')) x0 = snap(box.x + x0, st) - box.x;
      if (h.includes('e')) x1 = snap(box.x + x1, st) - box.x;
      if (h.includes('n')) y0 = snap(box.y + y0, st) - box.y;
      if (h.includes('s')) y1 = snap(box.y + y1, st) - box.y;
    }
    // the moving edges stick to edges and centers of the objects around (not rotated boxes, not corners kept in proportion)
    d.guides = [];
    const stuck: { axis: 'x' | 'y'; at: number; t: Rect }[] = [];
    if (snapEdges && box.rotation === 0 && d.targets.length && !(keepAspect && h.length === 2)) {
      const tol = 6 / this.renderer.camera.zoom;
      const near = (v: number, axis: 'x' | 'y') => {
        let best: { d: number; at: number; t: Rect } | null = null;
        for (const t of d.targets) {
          const vs = axis === 'x' ? [t.x, t.x + t.w / 2, t.x + t.w] : [t.y, t.y + t.h / 2, t.y + t.h];
          for (const c of vs) {
            const dd = c - v;
            if (Math.abs(dd) <= tol && (!best || Math.abs(dd) < Math.abs(best.d))) best = { d: dd, at: c, t };
          }
        }
        return best;
      };
      if (h.includes('w') || h.includes('e')) {
        const west = h.includes('w');
        const hit = near(box.x + (west ? x0 : x1), 'x');
        if (hit) {
          if (west) x0 += hit.d;
          else x1 += hit.d;
          stuck.push({ axis: 'x', at: hit.at, t: hit.t });
        }
      }
      if (h.includes('n') || h.includes('s')) {
        const north = h.includes('n');
        const hit = near(box.y + (north ? y0 : y1), 'y');
        if (hit) {
          if (north) y0 += hit.d;
          else y1 += hit.d;
          stuck.push({ axis: 'y', at: hit.at, t: hit.t });
        }
      }
    }
    const minSize = 1;
    if (x1 - x0 < minSize) {
      if (h.includes('w')) x0 = x1 - minSize;
      else x1 = x0 + minSize;
    }
    if (y1 - y0 < minSize) {
      if (h.includes('n')) y0 = y1 - minSize;
      else y1 = y0 + minSize;
    }
    if (keepAspect) {
      const ar = box.w / Math.max(box.h, 1e-9);
      const nw = x1 - x0;
      const nh = y1 - y0;
      if (h.length === 2) {
        if (nw / ar > nh) {
          const t = nw / ar;
          if (h.includes('n')) y0 = y1 - t;
          else y1 = y0 + t;
        } else {
          const t = nh * ar;
          if (h.includes('w')) x0 = x1 - t;
          else x1 = x0 + t;
        }
      } else if (h === 'e' || h === 'w') {
        const t = nw / ar;
        const cy = box.h / 2;
        y0 = cy - t / 2;
        y1 = cy + t / 2;
      } else {
        const t = nh * ar;
        const cx = box.w / 2;
        x0 = cx - t / 2;
        x1 = cx + t / 2;
      }
    }
    for (const g of stuck) {
      d.guides.push(
        g.axis === 'x'
          ? { axis: 'x', at: g.at, from: Math.min(box.y + y0, g.t.y), to: Math.max(box.y + y1, g.t.y + g.t.h) }
          : { axis: 'y', at: g.at, from: Math.min(box.x + x0, g.t.x), to: Math.max(box.x + x1, g.t.x + g.t.w) },
      );
    }
    const sx = (x1 - x0) / Math.max(box.w, 1e-9);
    const sy = (y1 - y0) / Math.max(box.h, 1e-9);
    const c = Math.cos(box.rotation);
    const s = Math.sin(box.rotation);
    const cx0 = box.x + box.w / 2;
    const cy0 = box.y + box.h / 2;
    // point in the old box's local frame (unscaled) → world
    const frameToWorld = (nx: number, ny: number): Vec2 => {
      const ux = nx - box.w / 2;
      const uy = ny - box.h / 2;
      return { x: cx0 + ux * c - uy * s, y: cy0 + ux * s + uy * c };
    };
    // old local point → new world position after scaling
    const mapLocal = (lx: number, ly: number): Vec2 => frameToWorld(x0 + lx * sx, y0 + ly * sy);
    try {
      const changed = new Set<Id>();
      for (const [id, o] of d.orig) {
        if (o.type === CoreTypes.group) continue;
        if (o.type === CoreTypes.connector) continue;
        if (d.single) {
          const nw = o.w * sx;
          let nh = o.h * sy;
          let textStyle = o.style.text;
          if (o.type === CoreTypes.text && textStyle) {
            if (scalesText) textStyle = { ...textStyle, fontSize: Math.max(0.01, textStyle.fontSize * sx) };
            // wrapped text stays inside the frame: side handles follow the lines,
            // the other handles can only add room below them
            const t = (o.props['text'] as string) ?? '';
            const need = textBoxHeight(t, readSpans(t, o.props['spans']), textStyle, nw);
            nh = h === 'e' || h === 'w' ? need : Math.max(nh, need);
          }
          const nc = frameToWorld((x0 + x1) / 2, y0 + nh / 2);
          const patch: Partial<ObjectRecord> = { x: nc.x - nw / 2, y: nc.y - nh / 2, w: nw, h: nh };
          if (textStyle !== o.style.text) patch.style = { ...o.style, text: textStyle };
          if (o.type === CoreTypes.line || o.type === CoreTypes.pen || o.type === CoreTypes.path) patch.props = scaledGeometryProps(o, sx, sy);
          d.tx.update(id, patch);
        } else {
          // multi-selection (axis-aligned box): scale centers and sizes; rotation kept
          const ocx = o.x + o.w / 2;
          const ocy = o.y + o.h / 2;
          const nc = mapLocal(ocx - box.x, ocy - box.y);
          const rotated = Math.abs(Math.sin(o.rotation * 2)) > 1e-6;
          const k = rotated ? Math.min(sx, sy) : 1;
          const nw = rotated ? o.w * k : o.w * sx;
          const nh = rotated ? o.h * k : o.h * sy;
          const swap = !rotated && Math.abs(Math.sin(o.rotation)) > 0.5;
          const fw = swap ? o.w * sy : nw;
          const fh = swap ? o.h * sx : nh;
          const patch: Partial<ObjectRecord> = { x: nc.x - fw / 2, y: nc.y - fh / 2, w: fw, h: fh };
          if (o.type === CoreTypes.line || o.type === CoreTypes.pen || o.type === CoreTypes.path) patch.props = scaledGeometryProps(o, fw / Math.max(o.w, 1e-9), fh / Math.max(o.h, 1e-9));
          d.tx.update(id, patch);
        }
        changed.add(id);
      }
      const parents = new Set<Id>();
      for (const [id, o] of d.orig) if (o.type === CoreTypes.group) parents.add(id);
      for (const id of this.selection) {
        const pid = this.doc.get(id)?.parentId;
        if (pid) parents.add(pid);
      }
      // update groups deepest first
      const depth = (id: Id) => {
        let n = 0;
        let cur = this.doc.get(id);
        while (cur?.parentId) {
          n++;
          cur = this.doc.get(cur.parentId);
        }
        return n;
      };
      for (const gid of [...parents].sort((a, b) => depth(b) - depth(a))) cmd.updateGroupBounds(d.tx, gid);
      this.rerouteFor(d.tx, changed);
    } catch (err) {
      this.cancelDrag();
      this.reportError(err);
    }
  }

  private applyRotate(d: Extract<Drag, { kind: 'rotate' }>, w: Vec2, snapAngle: boolean): void {
    let delta = Math.atan2(w.y - d.center.y, w.x - d.center.x) - d.startAngle;
    if (snapAngle) {
      const stepA = Math.PI / 12;
      if (d.single) {
        const o = [...d.orig.values()][0];
        delta = Math.round((o.rotation + delta) / stepA) * stepA - o.rotation;
      } else delta = Math.round(delta / stepA) * stepA;
    }
    const c = Math.cos(delta);
    const s = Math.sin(delta);
    try {
      const changed = new Set<Id>();
      for (const [id, o] of d.orig) {
        if (o.type === CoreTypes.group || o.type === CoreTypes.connector) continue;
        const ocx = o.x + o.w / 2 - d.center.x;
        const ocy = o.y + o.h / 2 - d.center.y;
        const ncx = d.center.x + ocx * c - ocy * s;
        const ncy = d.center.y + ocx * s + ocy * c;
        d.tx.update(id, { x: ncx - o.w / 2, y: ncy - o.h / 2, rotation: normalizeAngle(o.rotation + delta) });
        changed.add(id);
      }
      for (const [id, o] of d.orig) if (o.type === CoreTypes.group) cmd.updateGroupBounds(d.tx, id);
      this.rerouteFor(d.tx, changed);
    } catch (err) {
      this.cancelDrag();
      this.reportError(err);
    }
  }

  /** Screen rect of an object (for DOM overlays like the text editor and mode C iframes). */
  screenBox(id: Id): { x: number; y: number; w: number; h: number; rotation: number; zoom: number } | null {
    const o = this.doc.get(id);
    if (!o) return null;
    const cam = this.renderer.camera;
    const p = cam.worldToScreen(o.x + o.w / 2, o.y + o.h / 2);
    return { x: p.x - (o.w * cam.zoom) / 2, y: p.y - (o.h * cam.zoom) / 2, w: o.w * cam.zoom, h: o.h * cam.zoom, rotation: o.rotation, zoom: cam.zoom };
  }

  /** Generate many simple objects (benchmark / stress test). */
  generate(count: number, opts: { cols?: number; size?: number; gap?: number; withText?: boolean } = {}): void {
    const cols = opts.cols ?? Math.ceil(Math.sqrt(count));
    const size = opts.size ?? 80;
    const gap = opts.gap ?? 40;
    const types = [CoreTypes.rect, CoreTypes.ellipse, CoreTypes.triangle, CoreTypes.diamond];
    const colors = ['#fca5a5', '#fdba74', '#fde047', '#86efac', '#93c5fd', '#c4b5fd', '#f9a8d4', '#e5e7eb'];
    const keys = (() => {
      let top: string | null = null;
      for (const o of this.doc.allObjects()) if (top === null || o.z > top) top = o.z;
      const out: string[] = [];
      let k = top;
      // evenly spread keys: append sequence keeps keys short (integer part grows)
      for (let i = 0; i < count; i++) out.push((k = keyBetween(k, null)));
      return out;
    })();
    const c0 = this.renderer.camera;
    const ox = c0.cx - (cols * (size + gap)) / 2;
    const oy = c0.cy - (Math.ceil(count / cols) * (size + gap)) / 2;
    this.edit('generate', (tx) => {
      for (let i = 0; i < count; i++) {
        const type = types[i % types.length];
        const col = i % cols;
        const row = Math.floor(i / cols);
        tx.create(
          makeObject(this.doc.canvasId, {
            type,
            x: ox + col * (size + gap),
            y: oy + row * (size + gap),
            w: size,
            h: size * 0.75,
            z: keys[i],
            style: {
              stroke: { color: '#334155', width: 2, dash: i % 7 === 0 ? 'dashed' : 'solid' },
              fill: i % 11 === 0 ? { kind: 'lines', color: '#64748b', spacing: 8, angle: Math.PI / 4 } : { kind: 'solid', color: colors[i % colors.length] },
              opacity: 1,
            },
            props: opts.withText ? { text: 'Объект ' + (i + 1) } : { text: '' },
          }),
        );
      }
    });
  }
}

/** Point-based props of lines, strokes and bezier paths scaled with their box. */
/**
 * The key of a shortcut independent of the keyboard layout: letters come from the physical key when
 * the layout is not Latin (Ctrl+Z works on a Russian layout too), Latin layouts keep their own letters.
 */
export function shortcutKey(e: Pick<KeyboardEvent, 'key' | 'code' | 'altKey'>): string {
  const code = /^Key([A-Z])$/.exec(e.code);
  const key = e.key.toLowerCase();
  // Alt (AltGr) can turn a letter into another character; the physical key tells which one was meant
  if (code && (e.altKey || !/^[a-z]$/.test(key))) return code[1].toLowerCase();
  if (e.code === 'NumpadAdd') return '+';
  if (e.code === 'NumpadSubtract') return '-';
  return key;
}

function scaledGeometryProps(o: ObjectRecord, sx: number, sy: number): Record<string, unknown> {
  const out: Record<string, unknown> = { ...o.props, points: scalePoints(o.props['points'] as number[], sx, sy) };
  if (Array.isArray(o.props['nodes'])) out['nodes'] = scalePoints(o.props['nodes'] as number[], sx, sy);
  if (Array.isArray(o.props['holes'])) out['holes'] = (o.props['holes'] as number[][]).map((r) => scalePoints(r, sx, sy));
  return out;
}

function scalePoints(pts: number[] | undefined, sx: number, sy: number): number[] {
  if (!Array.isArray(pts)) return [0, 0, 1, 0];
  return pts.map((v, i) => (i % 2 === 0 ? v * sx : v * sy));
}

function normalizeAngle(a: number): number {
  const t = Math.PI * 2;
  a = a % t;
  if (a > Math.PI) a -= t;
  if (a < -Math.PI) a += t;
  return Math.abs(a) < 1e-9 ? 0 : a;
}


/** Style fields an object passes on ("copy style", "make default style"). */
export function pickStyle(style: Partial<Style>): Partial<Style> {
  const out: Partial<Style> = {};
  // JSON, not structuredClone: the style may come from reactive settings (a proxy cannot be cloned)
  if (style.fill) out.fill = JSON.parse(JSON.stringify(style.fill)) as Style['fill'];
  if (style.stroke !== undefined) out.stroke = style.stroke ? { ...style.stroke } : null;
  if (style.opacity !== undefined) out.opacity = style.opacity;
  if (style.cornerRadius !== undefined) out.cornerRadius = style.cornerRadius;
  if (style.text) out.text = { ...style.text };
  return out;
}

/** Sizes of a style (stroke width, font size, corner radius) times k. */
function scaleStyle(style: Partial<Style>, k: number): Partial<Style> {
  if (k === 1) return pickStyle(style);
  const round = (v: number) => parseFloat(v.toPrecision(2));
  const out = pickStyle(style);
  if (out.stroke) out.stroke.width = round(out.stroke.width * k);
  if (out.text) out.text.fontSize = round(out.text.fontSize * k);
  if (out.cornerRadius) out.cornerRadius = round(out.cornerRadius * k);
  return out;
}

/**
 * Equal spacing for a box being moved along an axis: between its two neighbors in the same row
 * (column), or one gap further from a neighbor pair with the same gap. Returns the shift and the gaps to show.
 */
export function equalSpacing(b: Rect, targets: Rect[], axis: 'x' | 'y', tol: number): { d: number; gaps: GapGuide[] } | null {
  const lo = (r: Rect) => (axis === 'x' ? r.x : r.y);
  const len = (r: Rect) => (axis === 'x' ? r.w : r.h);
  const hi = (r: Rect) => lo(r) + len(r);
  const across = (r: Rect) => (axis === 'x' ? [r.y, r.y + r.h] : [r.x, r.x + r.w]);
  const [b0, b1] = across(b);
  const row = targets.filter((t) => {
    const [t0, t1] = across(t);
    return t0 < b1 && t1 > b0;
  });
  if (!row.length) return null;
  const before = row.filter((t) => hi(t) <= lo(b) + tol).sort((p, q) => hi(q) - hi(p));
  const after = row.filter((t) => lo(t) >= hi(b) - tol).sort((p, q) => lo(p) - lo(q));
  const mid = (p: Rect, q: Rect) => {
    const [p0, p1] = across(p);
    const [q0, q1] = across(q);
    return (Math.max(p0, q0) + Math.min(p1, q1)) / 2;
  };
  const at = (pos: number): Rect => (axis === 'x' ? { ...b, x: pos } : { ...b, y: pos });
  const cands: { pos: number; gaps: (pos: number) => GapGuide[] }[] = [];
  const L = before[0];
  const Rn = after[0];
  if (L && Rn && lo(Rn) - hi(L) >= len(b)) {
    cands.push({ pos: (hi(L) + lo(Rn) - len(b)) / 2, gaps: (p) => [{ axis, from: hi(L), to: p, at: mid(L, at(p)) }, { axis, from: p + len(b), to: lo(Rn), at: mid(at(p), Rn) }] });
  }
  if (L) {
    const LL = row.filter((t) => hi(t) <= lo(L) + 0.001).sort((p, q) => hi(q) - hi(p))[0];
    const g = LL ? lo(L) - hi(LL) : 0;
    if (LL && g > 0) cands.push({ pos: hi(L) + g, gaps: (p) => [{ axis, from: hi(LL), to: lo(L), at: mid(LL, L) }, { axis, from: hi(L), to: p, at: mid(L, at(p)) }] });
  }
  if (Rn) {
    const RR = row.filter((t) => lo(t) >= hi(Rn) - 0.001).sort((p, q) => lo(p) - lo(q))[0];
    const g = RR ? lo(RR) - hi(Rn) : 0;
    if (RR && g > 0) cands.push({ pos: lo(Rn) - g - len(b), gaps: (p) => [{ axis, from: hi(Rn), to: lo(RR), at: mid(Rn, RR) }, { axis, from: p + len(b), to: lo(Rn), at: mid(at(p), Rn) }] });
  }
  let best: { d: number; pos: number; gaps: (pos: number) => GapGuide[] } | null = null;
  for (const c of cands) {
    const d = c.pos - lo(b);
    if (Math.abs(d) <= tol && (!best || Math.abs(d) < Math.abs(best.d))) best = { d, ...c };
  }
  return best ? { d: best.d, gaps: best.gaps(best.pos) } : null;
}

function pointsBounds(pts: number[]): Rect {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i < pts.length; i += 2) {
    x0 = Math.min(x0, pts[i]);
    x1 = Math.max(x1, pts[i]);
    y0 = Math.min(y0, pts[i + 1]);
    y1 = Math.max(y1, pts[i + 1]);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}


const corners = (b: Rect): [number, number][] => [
  [b.x, b.y],
  [b.x + b.w, b.y],
  [b.x + b.w, b.y + b.h],
  [b.x, b.y + b.h],
];

/** The whole box inside the lasso: its corners inside and no lasso point inside it (the outline does not cut it). */
export function boxInPolygon(b: Rect, pts: number[]): boolean {
  if (!corners(b).every(([x, y]) => pointInPolygon(x, y, pts))) return false;
  for (let i = 0; i < pts.length; i += 2) if (pts[i] > b.x && pts[i] < b.x + b.w && pts[i + 1] > b.y && pts[i + 1] < b.y + b.h) return false;
  return true;
}

/** The lasso touches the box: a corner or the centre inside the lasso, or a lasso point inside the box. */
export function boxTouchesPolygon(b: Rect, pts: number[]): boolean {
  if (pointInPolygon(b.x + b.w / 2, b.y + b.h / 2, pts) || corners(b).some(([x, y]) => pointInPolygon(x, y, pts))) return true;
  for (let i = 0; i < pts.length; i += 2) if (pts[i] >= b.x && pts[i] <= b.x + b.w && pts[i + 1] >= b.y && pts[i + 1] <= b.y + b.h) return true;
  return false;
}
